<!-- Copyright (c) 2025 Digital Systems (M) Sdn. Bhd. -->

<template>
  <div class="mrt-main">
    <div class="left-menu" ref="leftMenu">
      <Menus :menu="menu" @menuClicked="menuClicked" />
    </div>

    <div class="right-container">
      <div class="top-section">
        <div class="filter-desc">{{ filterText }}</div>
        <ButtonFilter
          class="button-filter-component"
          filterPosition="right"
          buttonText="Change Filter"
          :filterWidth="300"
          :isOpen.sync="listingFilterIsOpen"
          :buttonIsDisabled="listingFilterIsDisabled"
        >
          <div class="filter-popup">
            <BoxContainer
              title="Date"
              :isCheckBox="true"
              style="margin-bottom: 5px"
              :isChecked.sync="filterListingByDate"
            >
              <div>
                <DateTimePicker
                  ref="fromdate"
                  placeholder="From"
                  :isRequired="filterListingByDate"
                  :showTime="false"
                  :isDisabled="!filterListingByDate"
                  :value.sync="mrtListFromDate"
                  :errorMessage.sync="fromDateErrMsg"
                />
                <DateTimePicker
                  ref="todate"
                  placeholder="To"
                  :isRequired="filterListingByDate"
                  :showTime="false"
                  :isDisabled="!filterListingByDate"
                  :value.sync="mrtListToDate"
                  :errorMessage.sync="toDateErrMsg"
                />
              </div>
            </BoxContainer>
            <BoxContainer
              title="Search"
              :isCheckBox="true"
              style="margin-bottom: 5px"
              :isChecked.sync="filterListingBySearch"
            >
              <div>
                <Dropdown
                  ref="searchby"
                  placeholder="Search By"
                  :items="searchFilterItems"
                  itemKey="Search By"
                  :isDisabled="!filterListingBySearch"
                  :isRequired="filterListingBySearch"
                  :selectedObject.sync="selectedSearchBy"
                  :errorMessage.sync="searchByErr"
                />
                <Dropdown
                  ref="searchcriteria"
                  placeholder="Search Criteria"
                  :items="searchCriteriaItems"
                  itemKey="name"
                  :isDisabled="!filterListingBySearch"
                  :isRequired="filterListingBySearch"
                  :selectedObject.sync="selectedSearchCriteria"
                  :errorMessage.sync="searchCriteriaErr"
                />
                <Input
                  ref="search"
                  placeholder="Search"
                  :inputValue.sync="searchValue"
                  :isDisabled="!filterListingBySearch"
                  :isRequired="filterListingBySearch"
                  :errorMessage.sync="searchValueErr"
                />
              </div>
            </BoxContainer>
            <button @click="refreshClicked" :disabled="!filterListingByDate && !filterListingBySearch">Refresh</button>
          </div>
        </ButtonFilter>
        <div>Last Refreshed: {{ lastRefreshed }}</div>
      </div>
      <div class="bottom-section">
        <TableBox
          title="Misc Receipt"
          :tableData="miscRtListing"
          :dataType="miscRtListingDataType"
          :selectedRow.sync="selectedMRTListing"
          :loading="mrtTableBoxLoading"
          style="height: 100%"
        />
      </div>
    </div>

    <PopupEntry
      v-if="showPopup"
      :show.sync="showPopup"
      :title="popupTitle"
      :createdBy="createdBy"
      :updatedBy="updatedBy"
      @save="saveClicked"
      :requiredError="requiredError"
      @requiredClicked="requiredClicked"
      :successMessages="successMessages"
      :errorMessages="errorMessages"
      :viewMode="viewMode"
      :cancelButton="viewMode ? 'Close' : 'Cancel'"
      style="display: flex; flex-direction: row; justify-content: space-around; overflow: scroll"
      :canPrint="false"
      @print="popupPrintClicked"
    >
      <div class="mrt-entries">
        <!-- Summary of Events -->
        <BoxContainer
          v-if="action != 'N'"
          title="Summary of Events"
          :isCollapsible="true"
          :isCollapsed="summaryIsCollapsed"
        >
          <div class="summary-row">
            <div class="summary-block" v-for="(m, i) in showSummaryItems" :key="i">
              <Tooltip class="milestone-tooltip" :minWidth="210" :tooltipText="m['Label'].join(', ')">
                <div></div>
              </Tooltip>
              <div
                :id="m['Title']"
                v-if="m['Title'] != 'Print Count' && m['Title'] != 'Reinstate'"
                class="summary-item"
              >
              </div>
              <div
                v-if="
                  m['Title'] == 'Reinstate' && miscRtDetails['LatestAudit'] == 'R' && miscRtDetails['Indicator'] != 'C'
                "
                class="summary-item"
                :style="{ backgroundColor: m['BgColor'] }"
              >
                Reinstated
              </div>
              <div v-if="m['Title'] == 'Print Count'" class="summary-item">
                {{ miscRtDetails['Print Count'] }}
                <img :src="m['Src']" height="20px" />
              </div>
            </div>
          </div>
          <div class="summary-row">
            <div v-for="(bg, val, i) in newLegend" :key="i" class="legend-items">
              <div class="legend-box" :style="{ backgroundColor: bg }"></div>
              {{ val }}
            </div>
          </div>
        </BoxContainer>

        <!-- Header -->
        <BoxContainer title="Misc. Receipt Header" :isCollapsible="true" style="padding-bottom: 5px">
          <div class="mrt-header">
            <div class="header-containers" style="width: 30%">
              <InputQuery
                ref="payeetype"
                placeholder="Payee Type"
                :items="payeeTypeList"
                itemKey="Code - Party"
                :selectedObject.sync="selectedPayeeType"
                :errorMessage.sync="payeeTypeErr"
                :isRequired="true"
                :isDisabled="viewMode"
                @selected="payeeTypeSelected"
                validator="text,10"
              />
              <InputQuery
                ref="payeecode"
                placeholder="Payee Code"
                :items="payeeCodeList"
                :itemKey="payeeCodeKey"
                :selectedObject.sync="selectedPayeeCode"
                :errorMessage.sync="payeeCodeErr"
                :isRequired="true"
                :isDisabled="viewMode || !selectedPayeeType || disablePayeeCode"
                @selected="payeeCodeSelected"
                validator="text,10"
              />
              <Input
                ref="payeename"
                placeholder="Payee"
                :inputValue.sync="payeeName"
                :errorMessage.sync="payeeNameErr"
                :isRequired="true"
                :isDisabled="viewMode"
                validator="text,100"
              />
              <InputQuery
                ref="paymenttype"
                placeholder="Payment Type"
                :items="paymentTypeList"
                itemKey="Payment Type - Description"
                :selectedObject.sync="selectedPaymentType"
                :errorMessage.sync="paymentTypeErr"
                :isRequired="true"
                :isDisabled="viewMode"
                @selected="paymentTypeSelected"
              />
              <BoxContainer title="Cheque Info" :isCollapsible="true">
                <Input
                  ref="chequeno"
                  placeholder="Cheque No."
                  :inputValue.sync="chequeNo"
                  :errorMessage.sync="chequeNoErr"
                  :isRequired="showChequeInfo"
                  :isDisabled="viewMode || !showChequeInfo"
                  validator="text,25,,,spacedash"
                />
                <DateTimePicker
                  ref="chequedate"
                  placeholder="Cheque Date"
                  :value.sync="chequeDate"
                  :isRequired="showChequeInfo"
                  :showTime="false"
                  :errorMessage.sync="chequeDateErr"
                  :minDate="new Date('1900-01-01')"
                  :maxDate="new Date('2079-06-06')"
                  :isDisabled="viewMode || !showChequeInfo"
                />
                <Input
                  ref="chequebank"
                  placeholder="Cheque Bank"
                  :inputValue.sync="chequeBank"
                  :errorMessage.sync="chequeBankErr"
                  :isDisabled="viewMode || !showChequeInfo"
                  validator="text,10"
                />
              </BoxContainer>
            </div>
            <div class="header-containers" style="width: 45%">
              <div>
                <InputQuery
                  ref="bank"
                  placeholder="Bank In Bank"
                  :items="bankList"
                  itemKey="BankCode - BankName"
                  :selectedObject.sync="selectedBank"
                  :errorMessage.sync="bankErr"
                  :isRequired="true"
                  :isDisabled="viewMode"
                  @selected="bankSelected"
                  validator="text,10"
                />
                <Dropdown
                  placeholder="Self-Billed"
                  :items="selfBilledOptions"
                  itemKey="Type"
                  :selectedObject.sync="selectedSelfBilledType"
                  :isDisabled="viewMode"
                />
              </div>
              <div class="foreign-currency-main" style="margin: 5px 0px">
                <div class="foreign-currency-controls">
                  <div class="foreign-currency-row">
                    <div class="foreign-currency-column"></div>
                    <div class="foreign-currency-column" style="text-align: center"></div>
                    <div class="foreign-currency-column" style="text-align: center">MYR Equivalent</div>
                  </div>
                  <BoxContainer background="rgba(255,255,255)">
                    <div class="foreign-currency-row">
                      <div class="foreign-currency-column">
                        <div class="foreign-currency-row">
                          <InputQuery
                            ref="currency"
                            placeholder="Currency"
                            :items="currencyList"
                            itemKey="Currency Code"
                            :selectedObject.sync="selectedCurrency"
                            :errorMessage.sync="currencyErr"
                            :isRequired="true"
                            :isDisabled="viewMode || crGLAccDetails.length > 0"
                            @selected="currencySelected"
                            validator="text,10"
                            style="width: 70%"
                          />
                          <Input
                            ref="factor"
                            placeholder="Factor"
                            :inputValue.sync="exchFactor"
                            :errorMessage.sync="exchFactorErr"
                            :isDisabled="true"
                            style="padding-right: 5px; width: 30%"
                          />
                        </div>
                      </div>
                      <div class="foreign-currency-column">
                        <div class="foreign-currency-row">
                          <Input
                            ref="payamt"
                            placeholder="Payment Amt."
                            :inputValue.sync="paymentFAmt"
                            :errorMessage.sync="paymentFAmtErr"
                            :isRequired="true"
                            @focusChanged="headerForeignCurrLossFocus($event, 'paymentFAmt', paymentFAmt)"
                            :isDisabled="viewMode"
                            validator="positiveDecimal,14,4"
                            style="padding-left: 5px"
                          />
                          <Input
                            ref="rate"
                            placeholder="Exch. Rate"
                            :inputValue.sync="exchRate"
                            :errorMessage.sync="exchRateErr"
                            :isRequired="true"
                            @focusChanged="headerForeignCurrLossFocus($event, 'exchRate', exchRate)"
                            :isDisabled="viewMode || disableExchRate"
                            validator="positiveDecimal,14,4"
                            style="padding-right: 5px"
                          />
                        </div>
                      </div>
                      <div class="foreign-currency-column">
                        <div class="foreign-currency-row">
                          <Input
                            ref="myramt"
                            placeholder="Local Amt."
                            :inputValue.sync="paymentAmt"
                            :errorMessage.sync="paymentAmtErr"
                            :isRequired="true"
                            validator="positiveDecimal,14,4"
                            @focusChanged="headerForeignCurrLossFocus($event, 'paymentAmt', paymentAmt)"
                            :isDisabled="viewMode"
                            style="padding-left: 5px"
                          />
                        </div>
                      </div>
                    </div>
                  </BoxContainer>
                  <BoxContainer background="rgba(255,255,255)">
                    <div class="foreign-currency-row">
                      <div class="foreign-currency-column">
                        <div class="foreign-currency-row"></div>
                      </div>
                      <div class="foreign-currency-column">
                        <div class="foreign-currency-row">
                          <Input
                            ref="fbankchrg"
                            placeholder="F. Bank Charges"
                            :inputValue.sync="bankFCharge"
                            :errorMessage.sync="bankFChargeErr"
                            @focusChanged="headerForeignCurrLossFocus($event, 'fBankChrg', bankFCharge)"
                            :isDisabled="viewMode || disableFBankChrg"
                            validator="positiveDecimal,14,4"
                            style="padding: 0px 5px"
                          />
                          <Input
                            ref="rate"
                            placeholder="Exch. Rate"
                            :inputValue.sync="exchRate"
                            :errorMessage.sync="exchRateErr"
                            :isRequired="true"
                            :isDisabled="true"
                            validator="positiveDecimal,14,4"
                            style="padding-right: 5px"
                          />
                        </div>
                      </div>
                      <div class="foreign-currency-column">
                        <div class="foreign-currency-row">
                          <Input
                            ref="bankchrg"
                            placeholder="Local Bank Charges"
                            :inputValue.sync="bankCharge"
                            :errorMessage.sync="bankChargeErr"
                            @focusChanged="headerForeignCurrLossFocus($event, 'bankChrg', bankCharge)"
                            :isDisabled="viewMode"
                            validator="positiveDecimal,14,4"
                            style="padding-left: 5px"
                          />
                        </div>
                      </div>
                    </div>
                  </BoxContainer>
                  <BoxContainer background="rgba(255,255,255)" v-if="GSTEffective">
                    <div class="foreign-currency-row">
                      <div class="foreign-currency-column">
                        <div class="foreign-currency-row">
                          <InputQuery
                            ref="taxcode"
                            placeholder="Tax Code"
                            :items="taxCodeList"
                            itemKey="Tax Code - Description"
                            :selectedObject.sync="selectedTaxCode"
                            @selected="taxCodeSelected"
                            :isDisabled="viewMode"
                            validator="text,10"
                          />
                          <Input
                            ref="taxrate"
                            placeholder="Tax Rate"
                            :inputValue.sync="taxRate"
                            :errorMessage.sync="taxRateErr"
                            :isDisabled="viewMode"
                            validator="positiveInteger,4"
                            @focusChanged="headerForeignCurrLossFocus($event, 'taxRate', taxRate)"
                            style="padding-right: 5px"
                          />
                        </div>
                      </div>
                      <div class="foreign-currency-column">
                        <div class="foreign-currency-row">
                          <Input
                            ref="ftaxamt"
                            placeholder="F. Tax Amt."
                            :inputValue.sync="fTaxAmt"
                            :errorMessage.sync="fTaxAmtErr"
                            :isDisabled="true"
                            validator="decimal,14,4"
                            style="padding: 0px 5px"
                          />
                        </div>
                      </div>
                      <div class="foreign-currency-column">
                        <div class="foreign-currency-row">
                          <Input
                            ref="taxamt"
                            placeholder="Tax Amt"
                            :inputValue.sync="taxAmt"
                            :errorMessage.sync="taxAmtErr"
                            :isDisabled="true"
                            validator="decimal,14,4"
                            style="padding-left: 5px"
                          />
                        </div>
                      </div>
                    </div>
                  </BoxContainer>
                </div>
                <div class="foreign-currency-background">
                  <div class="background-left"></div>
                  <div class="background-middle"></div>
                  <div class="background-right"></div>
                </div>
              </div>
            </div>
            <div class="header-containers" style="width: 25%">
              <Input
                ref="mreceiptno"
                placeholder="Misc. Receipt No"
                :inputValue.sync="miscRtNo"
                :errorMessage.sync="miscRtNoErr"
                :isRequired="true"
                :isDisabled="action != 'N' || disabledMRTNo"
                validator="text,25"
              />
              <DateTimePicker
                ref="mreceiptdate"
                placeholder="Misc. Receipt Date"
                :value.sync="miscRtDate"
                :errorMessage.sync="miscRtDateErr"
                :showTime="false"
                :isRequired="true"
                :isDisabled="viewMode"
                :minDate="new Date('1900-01-01')"
                :maxDate="new Date('2079-06-06')"
              />
              <RemarksBox
                title="Remarks"
                :text.sync="remarks"
                :isDisabled="viewMode"
                :maxLength="200"
                style="min-height: 65%; max-height: 70%"
              />
            </div>
          </div>
        </BoxContainer>

        <div style="height: 100%; margin-top: 10px">
          <Tab
            :preventTabSwitch="true"
            :tabs="tabsItems"
            :tabIndex.sync="currentTabIndex"
            @tabClicked="tabClicked"
            style="height: 100%"
          >
            <div slot="Credit GL Account" class="tab-slot">
              <div class="tab-details-styling" :style="{ width: showCrGLInput ? '50%' : '100%' }">
                <div class="tab-buttons">
                  <button v-if="!viewMode || action != ''" @click="openCrGL" :disabled="showCrGLInput"
                    >Add Credit Details</button
                  >
                  <button v-if="!viewMode || action != ''" @click="deleteCrGL">Delete Credit Details</button>
                </div>
                <TableBox
                  title="Credit GL Account"
                  v-if="showCrGLAccList"
                  :tableData="crGLAccDetails"
                  :dataType="crGLAccDetailsTableDataType"
                  :selectedRow.sync="selectedCrGLDetail"
                  :loading="crGLAccDetailLoading"
                  :maxSelection="1"
                  style="height: calc(100% - 40px)"
                  :hideColumns="['Receipt No', 'Currency', 'Rate', 'HeaderTxnID', 'RFTxnID']"
                />
              </div>

              <div v-if="showCrGLInput" class="side-entry-open" :style="{ width: showCrGLInput ? '50%' : '0%' }">
                <div class="title-button">
                  <b>Add Credit GL Account</b>
                  <div class="buttons-arr">
                    <button @click="addCrGLAccClicked">Add</button>
                    <button @click="cancelCrGlAccClicked">Cancel</button>
                  </div>
                </div>
                <div style="margin-right: 10px">
                  <div style="margin-bottom: 10px">
                    <div style="width: 100%; margin-right: 10px; padding-top: 10px">
                      <InputQuery
                        ref="accno"
                        placeholder="Acc No"
                        :items="crAccList"
                        itemKey="Acc No - Description"
                        :selectedObject.sync="selectedAcc"
                        :errorMessage.sync="accErr"
                        :isDisabled="viewMode"
                        :isRequired="true"
                      />
                      <div style="display: flex">
                        <RadioButtonSet
                          title="Document Type"
                          :buttons="docType"
                          labelKey="label"
                          :selected.sync="selectedDocType"
                          :isHorizontal="true"
                          :isDisabled="viewMode"
                          @radioChanged="docTypeRadioChanged"
                          style="margin-right: 5px; width: 50%"
                        />
                        <InputQuery
                          :placeholder="selectedDocType ? selectedDocType['label'] : 'Job No'"
                          :items="getDocList"
                          :itemKey="getDocListKey"
                          :selectedObject.sync="selectedDoc"
                          :isDisabled="viewMode"
                          style="width: 50%; margin-left: 10px; padding-top: 15px"
                          :showRowLimit="selectedDocType['keyValue'] == 'JOB'"
                          :rowLimitInputValue.sync="jobnoRowLimit"
                          @rowLimitFocusChanged="jobnoRowLimitChanged"
                        />
                      </div>
                      <InputQuery
                        placeholder="Payment No"
                        :items="paymentNoList"
                        itemKey="Payment No"
                        :selectedObject.sync="selectedPaymentNo"
                        :showRowLimit="true"
                        :rowLimitInputValue.sync="paymentnoRowLimit"
                        @rowLimitFocusChanged="paymentnoRowLimitChanged"
                        :allowFreeText="true"
                        :search.sync="selectedPaymentNoFreeText"
                        :isDisabled="viewMode"
                      />
                      <RemarksBox
                        title="Remarks"
                        :maxLength="200"
                        :text.sync="creditRemarks"
                        :isDisabled="viewMode"
                        style="margin-bottom: 10px; height: 100px"
                      />
                    </div>
                  </div>
                  <div class="foreign-currency-main" style="margin-top: 15px">
                    <div class="foreign-currency-controls">
                      <div class="foreign-currency-row">
                        <div class="foreign-currency-column"></div>
                        <div class="foreign-currency-column" style="text-align: center"></div>
                        <div class="foreign-currency-column" style="text-align: center">MYR Equivalent</div>
                      </div>
                      <BoxContainer background="rgba(225, 255, 255)">
                        <div class="foreign-currency-row">
                          <div class="foreign-currency-column">
                            <div class="foreign-currency-row">
                              <InputQuery
                                ref="offsetcurr"
                                placeholder="Currency"
                                :items="currencyList"
                                itemKey="Currency Code"
                                :selectedObject.sync="selectedCurrency"
                                :isRequired="true"
                                :isDisabled="true"
                                validator="text,3"
                              />
                            </div>
                          </div>
                          <div class="foreign-currency-column">
                            <div class="foreign-currency-row">
                              <Input
                                ref="crfamount"
                                placeholder="Credit F. Amt."
                                :inputValue.sync="creditFAmt"
                                :errorMessage.sync="creditFAmtErr"
                                :isDisabled="viewMode"
                                validator="decimal,14,4"
                                style="padding-left: 5px"
                                @focusChanged="creditFocusChanged($event, 'famt', creditFAmt)"
                              />
                              <Input
                                ref="offsetrate"
                                placeholder="Exch. Rate"
                                :inputValue.sync="exchRate"
                                :isDisabled="true"
                                validator="decimal,14,4"
                                style="padding-right: 5px"
                              />
                            </div>
                          </div>
                          <div class="foreign-currency-column">
                            <div class="foreign-currency-row">
                              <Input
                                ref="offsetamt"
                                placeholder="Credit Amt."
                                :inputValue.sync="creditAmt"
                                :errorMessage.sync="creditAmtErr"
                                :isDisabled="viewMode"
                                style="padding-left: 5px"
                                validator="decimal,14,4"
                                @focusChanged="creditFocusChanged($event, 'amt', creditAmt)"
                              />
                            </div>
                          </div>
                        </div>
                      </BoxContainer>
                    </div>
                    <div class="foreign-currency-background">
                      <div class="background-left"></div>
                      <div class="background-middle"></div>
                      <div class="background-right"></div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div slot="Attachments" class="tab-slot">
              <div class="tab-details-styling" style="width: 100%">
                <div style="display: flex; justify-content: end; margin-top: 5px">
                  <button v-if="!viewMode" @click="openAttachmentPopup('new')" style="margin-right: 10px"
                    >Add Attachment</button
                  >
                  <button
                    v-if="!viewMode && attachmentsList.length > 0"
                    @click="openAttachmentPopup('edit')"
                    style="margin-right: 10px"
                    >Edit Attachment</button
                  >
                  <button
                    v-if="!viewMode && attachmentsList.length > 0"
                    @click="deleteAttachmentClicked"
                    style="margin-right: 10px"
                    >Del Attachment</button
                  >
                </div>
                <TableBox
                  v-if="showAttTable"
                  title="Attachment"
                  :tableData="attachmentsList"
                  :selectedRow.sync="selectedAtt"
                  :additionalButtons="[{ text: 'View', index: 0, width: 60 }]"
                  @buttonClicked="attAdditionalBtnsClicked"
                  :hideColumns="[
                    'Action',
                    'Doc Image',
                    'Sequence No',
                    'Document GUID',
                    'ID',
                    'Doc Ref No',
                    'Job No',
                    'Doc Code',
                    'Doc Type',
                    'Doc Rec Date',
                    'Doc Rec Time',
                    '_attachmentAction',
                  ]"
                  :dataType="{ 'Doc Date': 'date' }"
                />
              </div>
            </div>

            <div slot="Events" class="tab-slot">
              <div class="tab-details-styling" style="width: 100%">
                <BoxContainer title="GL Entry">
                  <TableBox
                    :tableData="glEntryList"
                    title="GL Entry"
                    :maxSelection="0"
                    style="height: auto"
                    :dataType="glEntryTableDataType"
                  />
                  <div style="display: flex; justify-content: space-around; margin-top: 10px">
                    <div style="display: flex">
                      <div style="font-size: 1em; align-content: center">TOTAL DEBIT</div>
                      <div class="total-block" style="margin-left: 5px">{{ totalDebit }}</div>
                    </div>

                    <div style="display: flex">
                      <div style="font-size: 1em; align-content: center">TOTAL CREDIT</div>
                      <div class="total-block" style="margin-left: 5px">{{ totalCredit }}</div>
                    </div>
                  </div>
                </BoxContainer>
              </div>
            </div>
          </Tab>
        </div>
      </div>

      <BoxContainer>
        <div style="display: flex; flex-direction: row; justify-content: flex-end">
          <div style="display: flex; margin-right: 20px">
            <div style="align-content: center; margin-right: 10px; font-size: 1.1em">Total Credit Amount</div>
            <div style="display: flex">
              <div class="total-block" style="font-size: 1.1em"
                >{{ selectedCurrency ? selectedCurrency['Currency Code'] : 'MYR' }} {{ totalFOffset }}</div
              >
              <div
                class="total-block"
                style="background-color: #467cdc; color: white; font-size: 1.1em; font-weight: bold"
                >MYR {{ totalOffset }}</div
              >
            </div>
          </div>
          <div style="display: flex">
            <div style="align-content: center; margin-right: 10px; font-size: 1.1em">Misc. Receipt Balance</div>
            <div style="display: flex">
              <div class="total-block" style="font-size: 1.1em"
                >{{ selectedCurrency ? selectedCurrency['Currency Code'] : 'MYR' }} {{ miscRtFBalance }}</div
              >
              <div
                class="total-block"
                style="background-color: #467cdc; color: white; font-size: 1.1em; font-weight: bold"
                >MYR {{ miscRtBalance }}</div
              >
            </div>
          </div>
        </div>
      </BoxContainer>
    </PopupEntry>

    <PopupV2
      :show="showDeleteMiscRt"
      title="Delete Misc. Receipt"
      @buttonClicked="deletePopupClicked"
      :buttons="deletePopupButtons"
    >
      <div
        >Please state reason to delete
        {{ selectedMRTListing.length > 0 ? selectedMRTListing[0]['Misc. Receipt No'] : '' }}?</div
      >
      <Input
        placeholder="Delete Remarks"
        :inputValue.sync="deleteMiscRtRemarks"
        :errorMessage.sync="deleteMiscRtRemarksErr"
        :isRequired="true"
        validator="text,100"
      />
    </PopupV2>

    <div class="attachments-popup-container">
      <PopupV2
        class="attachments-popup"
        :show="showAttachmentsPopup"
        :title="attachmentsPopupTitle"
        :buttons="attachmentsPopupButtons"
        @buttonClicked="attachmentsPopupBtnsClicked"
      >
        <div class="attachments-popup-content">
          <Input
            textType="file"
            placeholder="Select File"
            ref="docFile"
            :isDisabled="viewMode"
            :inputValue.sync="documents['docName']"
            @fileChanged="handleFileUpload"
            :errorMessage.sync="attachmentsRequiredErrorMessage"
          />
          <div v-if="attachmentsAction == 'E'" class="attached-file">
            <strong>Attached file: </strong>
            <span
              class="file-link"
              title="Click to view"
              @click="
                projectAttachment({
                  'File Name': selectedAtt.length > 0 ? selectedAtt[0]['File Name'] : '',
                  'MIME Type': null,
                  'File Data': selectedAtt.length > 0 ? selectedAtt[0]['Doc Image'] : '',
                })
              "
            >
              {{ selectedAtt.length > 0 ? selectedAtt[0]['File Name'] : '' }}
            </span>
          </div>
          <RemarksBox
            :maxLength="200"
            style="height: 50%"
            title="Remarks"
            :isDisabled="viewMode"
            :text.sync="documents['remarks']"
          />
        </div>
      </PopupV2>
    </div>
    <div v-if="showAttachmentProjector" class="attachment-projector">
      <div class="attachment-projection">
        <object
          v-if="isProjectable"
          :data="attachmentProjectorData"
          :type="attachmentProjectorType"
          draggable="false"
        ></object>
        <div v-else class="attachment-not-projectable-container">
          <span>
            {{ selectedAtt[0]['File Type'].replaceAll('.', '').toUpperCase() }} files cannot be previewed. <br />
            Please download the file and view it instead.
          </span>
          <button @click="downloadAttachment">
            <img src="https://dsmlib.logistics.systems/Icons/Download.png" alt="Download icon" />
            Download
          </button>
        </div>
      </div>
      <div class="close-button" @click="closeProjector"> &times; </div>
    </div>
  </div>
</template>

<script>
export default {
  components: {},
  computed: {
    // So that summary UI reinstate part does not render if no reinstate
    showSummaryItems: function (){
      const copyOfSummaryItems = [...this.summaryItems];
      if (this.miscRtDetails['LatestAudit'] && this.miscRtDetails['LatestAudit'].trim() == 'R') {

        const reinstateItem = {
          Title: 'Reinstate',
          Label: [
            this.miscRtDetails['LastAuditUser'],
            new Date(this.miscRtDetails['AuditDate']).formatString('yyyy-MM-dd HH:mm'),
          ],
          Indicator: [],
          BgColor: '#79d279',
        };

        copyOfSummaryItems.splice(2, 0, reinstateItem);

      }
      const reconcillation = copyOfSummaryItems.find((item) => item['Title'] == 'recon');

      if(this.miscRtDetails['recon']  === 'S'){
        reconcillation['Label'] = [this.miscRtDetails['statement'], this.miscRtDetails['statementDate']];
      }
      else{
        reconcillation['Label'] = ['Reconcilled', 'Unreconcilled'];
      }
      
      return copyOfSummaryItems;
    },
    // Hide tax fields if not applicable based on SST date and GST date
    GSTEffective: function () {
      return (
        this.companyGSTEnabled == '1' &&
        new Date(this.companyGSTEffectiveDate) <= new Date(this.receiptDate) &&
        new Date(this.companySSTEffectiveDate) > new Date(this.receiptDate)
      );
    },

    // User not allowed to change exchange rate when already add cr details
    disableExchRate: function () {
      return (
        (this.selectedCurrency && this.selectedCurrency['Currency Code'] == 'MYR') || this.crGLAccDetails.length > 0
      );
    },

    disablePayeeCode: function () {
      return this.crGLAccDetails.length > 0 && this.crGLAccDetails.some((cr) => cr['Doc Type'] == 'CHQ');
    },

    disableFBankChrg: function () {
      return this.selectedBank && this.selectedBank['BankCurrency'] == 'MYR';
    },

    showChequeInfo: function () {
      return this.selectedPaymentType && this.selectedPaymentType['Payment Type'] == 'CH';
    },

    getDocList: function () {
      if (this.selectedDocType) {
        switch (this.selectedDocType['keyValue']) {
          case 'JOB':
            return this.jobList;

          case 'ASSET':
            return this.assetList;

          case 'CHQ': {
            let filteredChq = this.chequeList.filter(
              (chq) => !this.crGLAccDetails.map((cr) => cr['RFTxnID']).includes(chq['Txnid']),
            );
            filteredChq = filteredChq.filter((chq) => chq['Currency'] == this.selectedCurrency['Currency Code']);
            return filteredChq;
          }
        }
      }
      return [];
    },

    getDocListKey: function () {
      if (this.selectedDocType) {
        switch (this.selectedDocType['keyValue']) {
          case 'JOB':
            return 'Job #';

          case 'ASSET':
            return 'Asset Code';

          case 'CHQ':
            return 'Chq No';
        }
      }
      return '';
    },

    // Calculate total Foreign offset based on offset details list
    totalFOffset: function () {
      if (this.crGLAccDetails.length > 0) {
        const total = this.crGLAccDetails.reduce((acc, amt) => acc + Number(amt['F.Amount']), 0);
        return isNaN(total) ? '0.00' : this.$currencyFormat(total);
      }
      return '0.00';
    },

    // Calculate total offset (MYR) based on offset details list
    totalOffset: function () {
      if (this.crGLAccDetails.length > 0) {
        const total = this.crGLAccDetails.reduce((acc, amt) => acc + Number(amt['Amount']), 0);
        return isNaN(total) ? '0.00' : this.$currencyFormat(total);
      }
      return '0.00';
    },

    // Calculate total debit in GL Entry
    totalDebit: function () {
      if (this.glEntryList.length > 0) {
        const debits = this.glEntryList.filter((entry) => entry['Debit Ac'] != '0');
        const total = debits.reduce((acc, amt) => acc + Number(amt['Amount']), 0);
        return !isNaN(total) ? this.$currencyFormat(total) : '0.00';
      }
      return '0.00';
    },

    // Calculate total credit in GL Entry
    totalCredit: function () {
      if (this.glEntryList.length > 0) {
        const credits = this.glEntryList.filter((entry) => entry['Credit Ac'] != '0');
        const total = credits.reduce((acc, amt) => acc + Number(amt['Amount']), 0);
        return !isNaN(total) ? this.$currencyFormat(total) : '0.00';
      }
      return '0.00';
    },
  },
  data: function () {
    return {
      //#region Company Details
      companyGSTEnabled: '0',
      companySSTEffectiveDate: '', // Company.co_gsteffectivedate
      companyGSTEffectiveDate: '', // Company.co_gst2effectivedate
      //#end region

      menu: [
        {
          title: 'New Misc. Receipt',
          icon: 'https://dsmlib.logistics.systems/Icons/icons8-add-new-50.png',
          enabled: false,
          key: 'mnuAddNew',
        },
        {
          title: 'Edit Misc. Receipt',
          icon: 'https://dsmlib.logistics.systems/Icons/icons8-edit-64%20(1).png',
          enabled: false,
          key: 'mnuEdit',
        },
        {
          title: 'Delete Misc. Receipt',
          icon: 'https://dsmlib.logistics.systems/Icons/icons8-reduce-50.png',
          enabled: false,
          key: 'mnuDelete',
        },
        {
          title: 'View Misc. Receipt',
          icon: 'https://dsmlib.logistics.systems/Icons/icons8-view-64.png',
          enabled: false,
        },
        {
          title: 'Authorize Misc. Receipt',
          icon: 'https://dsmlib.logistics.systems/Icons/Approve.png',
          enabled: false,
          key: 'mnAuthorized',
          visible: true,
        },
        {
          title: 'Unauthorize Misc. Receipt',
          icon: 'https://dsmlib.logistics.systems/Icons/Approve.png',
          enabled: false,
          key: 'mnUnAuthorized',
          visible: false,
        },
        {
          title: 'Reinstate Misc. Receipt',
          icon: 'https://dsmlib.logistics.systems/Icons/icons8-loop-50.png',
          enabled: false,
          key: 'mnuReinstateMiscReceipt',
        },
        {
          title: 'Print Misc. Receipt',
          icon: 'https://dsmlib.logistics.systems/Icons/Print.png',
          enabled: true,
          key: 'mnuReceipt',
        },
      ],

      //#region Listing filter items
      filterText: '',
      lastRefreshed: '',
      listingFilterIsOpen: false,
      listingFilterIsDisabled: false,

      filterListingByDate: true,
      mrtListFromDate: new Date(),
      fromDateErrMsg: '',
      mrtListToDate: new Date(),
      toDateErrMsg: '',

      filterListingBySearch: false,
      searchFilterItems: [
        { 'Search By': 'Receipt #' },
        { 'Search By': 'Payee Name' },
        { 'Search By': 'Cheque #' },
        { 'Search By': 'Cheque Bank' },
        { 'Search By': 'Remarks' },
        { 'Search By': 'Job #' },
      ],
      selectedSearchBy: null,
      searchByErr: '',
      searchCriteriaItems: [{ name: 'EQUALS' }, { name: 'STARTS WITH' }, { name: 'CONTAINS' }, { name: 'ENDS WITH' }],
      selectedSearchCriteria: null,
      searchCriteriaErr: '',
      searchValue: '',
      searchValueErr: '',
      //#endregion

      //#region Misc Receipt Listing
      miscRtListing: [],
      miscRtListingDataType: {
        Total: 'currency',
        Balance: 'currency',
        'Misc. Receipt Date': 'date',
      },
      selectedMRTListing: [],
      mrtTableBoxLoading: false,
      //#endregion

      //#region Item lists
      payeeTypeList: [],
      payeeCodeList: [],
      payeeCodeKey: '',
      payeeCodeDescKey: '',
      paymentTypeList: [],
      bankList: [],
      selfBilledOptions: [{ Type: 'Self-Billed Refund Note', APIKey: '4' }],
      currencyList: [],
      taxCodeList: [],
      jobList: [],
      assetList: [],
      chequeList: [],
      docType: [
        { label: 'Job No', keyValue: 'JOB' },
        { label: 'Asset Code', keyValue: 'ASSET' },
        { label: 'Chq No', keyValue: 'CHQ', isDisabled: true },
      ],
      paymentNoList: [],
      crAccList: [],
      //#endregion

      //#region settings system
      disabledMRTNo: true,
      //#endregion

      //#region Popup General
      showPopup: false,
      popupTitle: '',
      createdBy: '',
      updatedBy: '',
      requiredError: [],
      successMessages: [],
      errorMessages: [],
      viewMode: true,
      //#endregion

      //#region Summary of Events
      summaryIsCollapsed: false,
      summaryItems: [
        {
          Title: 'Indicator',
          Label: ['New', 'Printed', 'Cancelled'],
          Indicator: [
            { code: 'N', name: 'New Receipt', bg: '#79d279' },
            { code: 'P', name: 'Printed', bg: '#79d279' },
            { code: 'C', name: 'Cancelled', bg: 'FFFFFF' },
          ],
        },
        {
          Title: 'Authorized',
          Label: ['Authorized', 'Unauthorize'],
          Indicator: [
            { code: 'A', name: 'Authorized', bg: '#79d279' },
            { code: 'U', name: 'Unauthorize', bg: '#ffff99' },
          ],
        },
        {
          Title: 'recon',
          Label: ['Reconcilled', 'Unreconcilled'],
          Indicator: [
            { code: 'S', name: 'Reconcilled', bg: '#79d279' },
            { code: '', name: 'Unreconcilled', bg: '#ffff99'}
          ],
        },
        {
          Title: 'Print Count',
          Src: 'https://dsmlib.logistics.systems/Icons/Print.png',
          Label: ['Print Count'],
          Indicator: [{ code: '0', name: 'Print Count' }],
        }
      ],
      newLegend: {
        // eslint-disable-next-line vue/no-unused-properties -- used in template
        Completed: '#79d279',
        // eslint-disable-next-line vue/no-unused-properties -- used in template
        Pending: '#ffff99',
      },
      //#endregion

      //#region Misc Receipt Details
      miscRtDetails: [],
      miscRtNo: '',
      miscRtNoErr: '',
      miscRtDate: null,
      miscRtDateErr: '',
      remarks: '',
      selectedPayeeType: null,
      payeeTypeErr: '',
      selectedPayeeCode: null,
      payeeCodeErr: '',
      payeeName: '',
      payeeNameErr: '',
      selectedPaymentType: null,
      paymentTypeErr: '',
      selectedBank: null,
      bankErr: '',
      selectedSelfBilledType: null,
      selectedCurrency: null,
      currencyErr: '',
      exchFactor: '1',
      exchFactorErr: '',
      exchRate: '1.0000',
      exchRateErr: '',
      oldRate: '',
      paymentFAmt: '0.00',
      paymentFAmtErr: '',
      oldFAmt: '0.00',
      paymentAmt: '0.00',
      paymentAmtErr: '',
      oldAmt: '0.00',
      bankFCharge: '0.00',
      bankFChargeErr: '',
      bankCharge: '0.00',
      bankChargeErr: '',
      selectedTaxCode: null,
      taxRate: '0',
      taxRateErr: '',
      fTaxAmt: '0.00',
      fTaxAmtErr: '',
      taxAmt: '0.00',
      taxAmtErr: '',

      miscRtBalance: '0.00',
      miscRtFBalance: '0.00',

      chequeNo: '',
      chequeNoErr: '',
      chequeDate: null,
      chequeDateErr: '',
      chequeBank: '',
      chequeBankErr: '',
      //#endregion

      //#region Tabs
      tabsItems: [
        { title: 'Credit GL Account', disabled: false },
        { title: 'Attachments', disabled: false },
        { title: 'Events', disabled: false },
      ],
      currentTabIndex: 0,
      //#endregion

      //#region Cr GL Acc
      crGLAccDetails: [],
      deletedCrGLAcc: [],
      crGLAccDetailsTableDataType: {
        Amount: 'currency',
        'F.Amount': 'currency',
        'Date Updated': 'date',
        Rate: 'currency',
      },
      showCrGLInput: false,
      showCrGLAccList: true,
      selectedCrGLDetail: [],
      crGLAccDetailLoading: false,

      selectedAcc: null,
      accErr: '',
      selectedDocType: null,
      selectedDoc: null,
      selectedPaymentNo: null,
      selectedPaymentNoFreeText: '',
      creditFAmt: '0.00',
      creditFAmtErr: '',
      creditAmt: '0.00',
      creditAmtErr: '',
      creditRemarks: '',

      jobnoRowLimit: '1000',
      paymentnoRowLimit: '1000',
      //#endregion

      //#region Attachments
      attachmentsList: [],
      deletedAttList: [],
      showAttTable: true,
      selectedAtt: [],
      showAttachmentsPopup: false,
      attachmentsPopupTitle: '',
      attachmentsPopupButtons: [],
      documents: {
        docName: '',
        attachmentFile: [],
        fileType: '',
        docGuid: '',
        remarks: '',
        docID: 0,
      },
      oldDoc: '',
      attachmentsAction: '', // 'N', 'E', 'V'
      attachmentsRequiredErrorMessage: '',
      showAttachmentProjector: false,
      isProjectable: false,
      attachmentProjectorFileName: '',
      attachmentProjectorData: '',
      attachmentProjectorType: '',
      //#endregion

      //#region GL Entry
      glEntryList: [],
      glEntryTableDataType: {
        'Ref No': 'string',
        'Debit No': 'string',
        'Credit No': 'string',
        Amount: 'currency',
        'Job No': 'string',
        Vehicle: 'string',
        'Cost Code': 'string',
        Remarks: 'string',
      },
      //#endregion

      //#region Delete
      showDeleteMiscRt: false,
      deletePopupButtons: [
        { text: 'Delete', color: 'indianred', textColor: 'white' },
        { text: 'Cancel', color: 'lightgray', textColor: 'black' },
      ],
      deleteMiscRtRemarks: '',
      deleteMiscRtRemarksErr: '',
      //#endregion
    };
  },
  props: {},
  methods: {
    menuClicked: async function (e) {
      this.clearAllData();
      this.clearAllErrMsg();
      this.listingFilterIsOpen = false;
      switch (e['title']) {
        case 'New Misc. Receipt': {
          this.action = 'N';
          this.viewMode = false;
          this.showPopup = true;
          this.miscRtNo = this.disabledMRTNo ? '**********' : '';
          this.miscRtDate = new Date();
          this.selectedCurrency = this.currencyList.find((c) => c['Currency Code'] == 'MYR');

          this.popupTitle = e['title'];
          break;
        }

        case 'Edit Misc. Receipt': {
          if (this.selectedMRTListing.length == 0) {
            this.$showAlert('error', 'Edit Misc. Receipt', 'Please select a misc. receipt to edit');
            return;
          }
          this.action = 'E';
          this.viewMode = false;
          await this.getMiscReceipt(e['title']);
          this.showPopup = true;
          this.$nextTick(() => {
            this.loadSummaryOfEvents();
          });
          break;
        }

        case 'View Misc. Receipt': {
          if (this.selectedMRTListing.length == 0) {
            this.$showAlert('error', 'View Misc. Receipt', 'Please select a misc. receipt to view');
            return;
          }
          this.action = '';
          this.viewMode = true;
          await this.getMiscReceipt(e['title']);
          this.showPopup = true;
          this.$nextTick(() => {
            this.loadSummaryOfEvents();
          });
          break;
        }

        case 'Delete Misc. Receipt': {
          if (this.selectedMRTListing.length == 0) {
            this.$showAlert('error', 'Delete Misc. Receipt', 'Please select a misc. receipt to delete.');
            return;
          }

          if (this.selectedMRTListing[0].Indicator == 'C') {
            this.$showAlert(
              'error',
              'Selected Misc. Receipt Already Cancelled',
              'You are not allowed to delete a cancelled misc. receipt',
            );
            return;
          }

          this.deleteMiscRtRemarks = '';
          this.deleteMiscRtRemarksErr = '';
          this.showDeleteMiscRt = true;
          break;
        }

        case 'Reinstate Misc. Receipt': {
          if (this.selectedMRTListing.length == 0) {
            this.$showAlert('error', 'Reinstate Misc. Receipt', 'Please select a misc. receipt to reinstate');
            return;
          }

          if (this.selectedMRTListing[0].Indicator != 'C') {
            this.$showAlert(
              'error',
              'Selected Misc. Receipt Not Cancelled',
              'You are only allowed to reinstate cancelled misc. receipt!',
            );
            return;
          }

          const confirm = await this.$showAlert(
            'warning',
            'Reinstate Misc. Receipt',
            `Are you sure you want to reinstate for this Misc. Receipt ${this.selectedMRTListing[0]['Misc. Receipt No']}?`,
            [
              { text: 'Yes', color: 'red', textColor: 'white' },
              { text: 'No', color: 'lightgray', textColor: 'black' },
            ],
          );

          if (confirm == 'Yes') {
            this.$fullscreenLoading('Reinstating Misc. Receipt');
            const rt = this.selectedMRTListing[0];
            const dataToPost = {
              action: '',
              mreceiptno: rt['Misc. Receipt No'],
              reinstate: '1',
            };
            const results = await this.$apiCallV2('/MiscReceipt/SaveMiscReceipt', dataToPost);
            this.$fullscreenLoading();

            if (results[0]['Results'] == 0) {
              this.$showAlert(
                'success',
                `Reinstated!`,
                `Misc. Receipt ${rt['Misc. Receipt No']} was successfully reinstated.`,
              );
            } else {
              this.$showAlert('error', 'Reinstate Unsuccessful', `${results[0]['ErrMsg']}`);
            }
            this.miscRtListing = [];
            this.selectedMRTListing = [];
            this.$nextTick(async () => await this.refreshClicked());
          }
          break;
        }

        case 'Authorize Misc. Receipt': {
          if (this.selectedMRTListing.length == 0) {
            this.$showAlert('error', 'Authorize Misc. Receipt', 'Please select a misc. receipt to authorize.');
            return;
          } else if (this.selectedMRTListing[0].Indicator == 'C') {
            this.$showAlert(
              'error',
              'Selected Misc. Receipt Already Cancelled',
              'You are not allowed to authorize a cancelled misc. receipt',
            );
            return;
          }

          const confirmAuth = await this.$showAlert(
            'warning',
            'Authorize Misc. Receipt',
            `Are you sure you want to authorize for this Misc. Receipt ${this.selectedMRTListing[0]['Misc. Receipt No']}?`,
            [
              { text: 'Yes', color: 'green', textColor: 'white' },
              { text: 'No', color: 'red' },
            ],
          );

          if (confirmAuth == 'No') return;

          this.$fullscreenLoading('Authorizing Misc. Receipt');
          const dataToAuth = {
            action: 'A',
            docrefname: 'RECEIPT',
            docrefid: this.selectedMRTListing[0]['Misc. Receipt No'],
            date: new Date(this.selectedMRTListing[0]['Misc. Receipt Date']).formatString('yyyy-MM-dd'),
          };
          const authorizeResult = await this.$apiCallV2('/MiscReceipt/Authorization', dataToAuth);
          this.$fullscreenLoading();

          if (authorizeResult[0]['Results'] == 0) {
            this.$showAlert(
              'success',
              'Authorized',
              `Misc. Receipt ${this.selectedMRTListing[0]['Misc. Receipt No']} was successfully authorized!`,
            );
            this.miscRtListing = [];
            this.selectedMRTListing = [];
            this.$nextTick(async () => await this.refreshClicked());
          } else {
            this.$showAlert('error', 'Authorization Unsuccessful', `${authorizeResult[0]['ErrMsg']}`);
            this.miscRtListing = [];
            this.selectedMRTListing = [];
            this.$nextTick(async () => await this.refreshClicked());
          }
          break;
        }

        case 'Unauthorize Misc. Receipt': {
          if (this.selectedMRTListing.length == 0) {
            this.$showAlert('error', 'Unauthorize Misc. Receipt', 'Please select a misc. receipt to unauthorize!');
            return;
          } else if (this.selectedMRTListing[0]['Indicator'] == 'C') {
            this.$showAlert(
              'error',
              'Selected Misc. Receipt Already Cancelled',
              'You are not allowed to unauthorize cancelled misc. receipt!',
            );
            return;
          }

          const confirmUnauth = await this.$showAlert(
            'warning',
            'Unauthorize Misc. Receipt',
            `Are you sure you want to unauthorize for this Misc. Receipt ${this.selectedMRTListing[0]['Misc. Receipt No']}?`,
            [
              { text: 'Yes', color: 'green', textColor: 'white' },
              { text: 'No', color: 'red' },
            ],
          );
          if (confirmUnauth == 'No') return;

          this.$fullscreenLoading('Unauthorizing Misc. Receipt');
          const dataToUnauth = {
            action: 'U',
            docrefname: 'RECEIPT',
            docrefid: this.selectedMRTListing[0]['Misc. Receipt No'],
            date: new Date(this.selectedMRTListing[0]['Misc. Receipt Date']).formatString('yyyy-MM-dd'),
          };
          const unauthorizeResult = await this.$apiCallV2('/MiscReceipt/Authorization', dataToUnauth);
          this.$fullscreenLoading();

          if (unauthorizeResult[0]['Results'] == 0) {
            this.$showAlert(
              'success',
              'Unauthorized',
              `Misc. Receipt ${this.selectedMRTListing[0]['Misc. Receipt No']} was successfully unauthorized!`,
            );
            this.miscRtListing = [];
            this.selectedMRTListing = [];
            this.$nextTick(async () => await this.refreshClicked());
          } else {
            this.$showAlert('error', 'Unauthorization Unsuccessful', `${unauthorizeResult[0]['ErrMsg']}`);
            this.miscRtListing = [];
            this.selectedMRTListing = [];
            this.$nextTick(async () => await this.refreshClicked());
          }
          break;
        }

        case 'Print Misc. Receipt':
          if (this.selectedMRTListing.length == 0) {
            this.openPrintReceipt();
          } else if (this.selectedMRTListing[0]['Indicator'] == 'C') {
            this.$showAlert(
              'error',
              'Selected Misc. Receipt Already Cancelled',
              'You are not allowed to print cancelled misc. receipt',
            );
          } else {
            this.openPrintReceipt(this.selectedMRTListing[0]['Misc. Receipt No']);
          }
          break;
      }
    },

    refreshClicked: async function () {
      let listingErr = false;

      if (this.filterListingByDate) {
        if (!this.mrtListFromDate) {
          this.fromDateErrMsg = 'Enter from date!';
          listingErr = true;
        }

        if (!this.mrtListToDate) {
          this.toDateErrMsg = 'Enter to date!';
          listingErr = true;
        }
      }

      if (this.filterListingBySearch) {
        if (!this.selectedSearchBy) {
          this.searchByErr = 'Select search by!';
          listingErr = true;
        }

        if (!this.selectedSearchCriteria) {
          this.searchCriteriaErr = 'Select search criteria!';
          listingErr = true;
        }

        if (!this.searchValue.trim()) {
          this.searchValueErr = 'Enter search value!';
          listingErr = true;
        }
      }

      if (listingErr) return;

      this.listingFilterIsOpen = false;
      const dataToFilter = {
        rttype: '1',
        startdate: this.filterListingByDate ? this.mrtListFromDate.formatString('yyyy-MM-dd') : '',
        enddate: this.filterListingByDate ? this.mrtListToDate.formatString('yyyy-MM-dd') : '',
        searchfield: this.filterListingBySearch ? this.selectedSearchBy['Search By'] : '',
        searchcriteria: this.filterListingBySearch ? this.selectedSearchCriteria['name'] : '',
        searchvalue: this.filterListingBySearch ? this.searchValue : '',
      };

      const filterItems = [];
      if (this.filterListingByDate) {
        filterItems.push(
          `date from ${this.mrtListFromDate.formatString('yyyy-MM-dd')} to ${this.mrtListToDate.formatString('yyyy-MM-dd')}`,
        );
      }
      if (this.filterListingBySearch) {
        filterItems.push(
          `${this.selectedSearchBy['Search By']} ${this.selectedSearchCriteria['name']} ${this.searchValue}`,
        );
      }
      this.filterText = `Filter by ${filterItems.join(', ')}`;

      this.$fullscreenLoading('Getting Misc Receipt Listing');
      this.mrtTableBoxLoading = true;
      this.miscRtListing = [];
      this.miscRtListing = await this.$apiCallV2('/MiscReceipt/GetReceiptListing', dataToFilter);
      this.lastRefreshed = new Date().formatString('yyyy-MM-dd HH:mm');
      this.mrtTableBoxLoading = false;
      this.$fullscreenLoading();
    },

    saveClicked: async function () {
      this.errorMessages = [];
      this.requiredError = [];

      // Validations
      if (this.showCrGLInput) {
        const discard = await this.promptDiscardCrGLAccInput();
        if (discard === 'Cancel') return;

        this.$nextTick(() => {
          this.showCrGLInput = false;
          this.clearAllCrGLAccDetails();
        });
      }

      if (this.checkInputFields()) return;

      // Check cheque date, cannot be lesser than 6 months
      if (this.chequeDate && this.getMonthDifference(new Date(this.miscRtDate), new Date(this.chequeDate)) <= -6) {
        const proceed = await this.$showAlert(
          'warning',
          'Cheque Month is Invalid',
          `Cheque Month: ${new Date(this.chequeDate).getMonth() + 1}/${new Date(this.chequeDate).getFullYear()}<br>Receipt Month: ${new Date(this.miscRtDate).getMonth() + 1}/${new Date(this.miscRtDate).getFullYear()}<br>Proceed?`,
          [
            { text: 'Yes', color: 'red', textColor: 'white' },
            { text: 'No', color: 'lightgray', textColor: 'black' },
          ],
        );

        if (proceed == 'Cancel') {
          this.chequeDateErr = 'Cheque Date invalid!';
          this.requiredError.push({ key: 'chequedate', value: this.chequeDateErr });
          return;
        }
      }

      if (this.crGLAccDetails.length == 0) {
        this.$showAlert(
          'error',
          'No Credit GL Account Transaction Added',
          'Please add at least 1 Credit GL Account transaction!',
        );
        return;
      }
      // Check if receipt amt lesser than balance
      if (Number(this.miscRtBalance) != 0) {
        this.$showAlert(
          'error',
          'Credit Total Amount not tally with Payment Amount',
          'Payment Amount and Credit Total Amount must tally!',
        );
        return;
      }

      // Dont need to concat deleted Credit Details for now, API will delete all ChequeGLPost and reinsert all in list
      const tempCredit = this.crGLAccDetails.map((c) => {
        return {
          action: 'N', // must be N to save
          accno: c['Account No'] || '',
          cramount: c['Amount'] || '0.00',
          crfamount: c['F.Amount'] || '0.00',
          crcurrency: c['Currency'] || '',
          crrate: c['Rate'] || '1.0000',
          doctype: c['Doc Type'] || '',
          docno: c['Document No'] || '',
          crremark: c['Remark'] || '',
          chqtxnid: c['RFTxnID'] || '',
          paymentno: c['Payment No'] || '',
        };
      });

      const allAttachments = this.attachmentsList.concat(this.deletedAttList);
      const tempAtt = allAttachments.map((doc) => {
        return {
          doccode: 'ATT',
          docrefno: '',
          remarks: doc['Doc Remark'],
          status: '',
          docname: doc['File Name'],
          docimage: doc['Doc Image'],
          docimagetype: doc['File Type'],
          action: doc['Action'] ?? this.action,
          docguid: this.action == 'N' ? '' : doc['Document GUID'],
        };
      });

      const dataToSave = {
        action: this.action,
        mreceiptno: this.action == 'N' && this.disabledMRTNo ? '' : this.miscRtNo,
        mreceiptdate: this.miscRtDate ? this.miscRtDate.formatString('yyyy-MM-dd') : '',
        payeetype: this.selectedPayeeType ? this.selectedPayeeType['Party Code'] : '',
        payeecode: this.selectedPayeeCode ? this.selectedPayeeCode[this.payeeCodeKey] : '',
        payee: this.payeeName || '',
        paymenttype: this.selectedPaymentType ? this.selectedPaymentType['Payment Type'] : '',
        paymentamt: this.paymentAmt || '0.00',
        fpaymentamt: this.paymentFAmt || '0.00',
        bank: this.selectedBank ? this.selectedBank['BankCode'] : '',
        chqno: this.selectedPaymentType?.['Payment Type'] == 'CH' ? this.chequeNo : '',
        chqdate:
          this.selectedPaymentType?.['Payment Type'] == 'CH' && this.chequeDate ?
            new Date(this.chequeDate).formatString('yyyy-MM-dd')
          : '',
        chqbank: this.selectedPaymentType?.['Payment Type'] == 'CH' ? this.chequeBank : '',
        bankchrg: this.bankCharge || '0.00',
        fbankchrg: this.bankFCharge || '0.00',
        remarks: this.remarks || '',
        currency: this.selectedCurrency ? this.selectedCurrency['Currency Code'] : '',
        rate: this.exchRate || '1.0000',
        taxcode: this.selectedTaxCode ? this.selectedTaxCode['Tax Code'] : '',
        taxrate: this.taxRate || '',
        taxamt: this.taxAmt || '',
        ftaxamt: this.fTaxAmt || '',
        reason: '',
        reinstate: '0',
        selfbilled: this.selectedSelfBilledType ? this.selectedSelfBilledType['APIKey'] : '',
        crgldetails: tempCredit ?? [],
        attachments: tempAtt ?? [],
      };

      this.$fullscreenLoading('Saving Misc. Receipt');
      const results = await this.$apiCallV2('/MiscReceipt/SaveMiscReceipt', dataToSave);
      this.$fullscreenLoading();

      if (results[0]['Results'] != 0) {
        if (this.action == 'N') {
          this.errorMessages.push(`There was an error creating Misc. Receipt: ${results[0]['ErrMsg']}`);
        } else {
          this.errorMessages.push(`There was an error editing Misc. Receipt: ${results[0]['ErrMsg']}`);
        }
        return;
      }

      this.showCrGLAccList = false;
      this.showAttTable = false;
      this.clearAllData();
      const mreceiptno = results[0]['Misc. Receipt No'];
      this.$nextTick(async () => {
        await this.populateMiscReceiptDetails(mreceiptno);
        this.loadSummaryOfEvents();
        this.showCrGLAccList = true;
        this.showAttTable = true;
        await this.refreshClicked();
      });

      this.successMessages.push(`${results[0]['ErrMsg']}`);
      this.viewMode = true;
      this.popupTitle = 'View Misc. Receipt';
      this.action = '';
    },

    requiredClicked: function (item) {
      this.$nextTick(() => {
        this.$refs[item].setFocus();
      });
    },

    tabClicked: async function (_tabItem, tabIndex) {
      if (this.showCrGLInput && tabIndex != 0) {
        const discard = await this.promptDiscardCrGLAccInput();
        if (discard === 'Cancel') {
          return;
        }
        this.$nextTick(() => {
          this.showCrGLInput = false;
          this.clearAllCrGLAccDetails();
        });
      }

      this.currentTabIndex = tabIndex;
    },

    popupPrintClicked: function () {
      if (this.miscRtDetails['Indicator'] == 'C') {
        this.$showAlert(
          'error',
          `Receipt ${this.miscRtNo} Already Cancelled`,
          'You are not allowed to print cancelled receipt',
        );
        return;
      }
      this.openPrintReceipt(this.miscRtNo);
    },

    clearAllData: function () {
      this.miscRtDetails = [];
      this.miscRtNo = '';
      this.miscRtDate = null;
      this.remarks = '';
      this.selectedPayeeType = null;
      this.selectedPayeeCode = null;
      this.payeeName = '';
      this.selectedPaymentType = null;
      this.selectedBank = null;
      this.selectedCurrency = null;
      this.exchFactor = '1';
      this.exchRate = '1.0000';
      this.oldRate = '';
      this.paymentFAmt = '0.00';
      this.oldFAmt = '0.00';
      this.paymentAmt = '0.00';
      this.oldAmt = '0.00';
      this.bankFCharge = '0.00';
      this.bankCharge = '0.00';
      this.selectedTaxCode = null;
      this.taxRate = '0';
      this.fTaxAmt = '0.00';
      this.taxAmt = '0.00';

      this.miscRtBalance = '0.00';
      this.miscRtFBalance = '0.00';

      this.chequeNo = '';
      this.chequeDate = null;
      this.chequeBank = '';

      this.crGLAccDetails = [];
      this.deletedCrGLAcc = [];
      this.attachmentsList = [];
      this.deletedAttList = [];
      this.glEntryList = [];

      this.successMessages = [];
      this.errorMessages = [];
      this.requiredError = [];

      this.showCrGLInput = false;

      this.clearAllCrGLAccDetails();
    },

    clearAllErrMsg: function () {
      this.miscRtNoErr = '';
      this.miscRtDateErr = '';
      this.payeeTypeErr = '';
      this.payeeCodeErr = '';
      this.payeeNameErr = '';
      this.paymentTypeErr = '';
      this.bankErr = '';
      this.currencyErr = '';
      this.exchFactorErr = '';
      this.exchRateErr = '';
      this.paymentFAmtErr = '';
      this.paymentAmtErr = '';
      this.bankFChargeErr = '';
      this.bankChargeErr = '';
      this.taxRateErr = '';
      this.fTaxAmtErr = '';
      this.taxAmtErr = '';
      this.chequeNoErr = '';
      this.chequeDateErr = '';
      this.chequeBankErr = '';
    },

    getMiscReceipt: async function (title) {
      this.popupTitle = title;
      if (this.selectedMRTListing.length > 0) {
        const mreceiptNo = this.selectedMRTListing[0]['Misc. Receipt No'];
        await this.populateMiscReceiptDetails(mreceiptNo);
      }
    },

    populateMiscReceiptDetails: async function (mreceiptNo) {
      this.$fullscreenLoading('Getting data');
      this.miscRtDetails = [];
      this.miscRtDetails = (
        await this.$apiCallV2('/MiscReceipt/GetReceiptDetails', { receiptno: mreceiptNo, ismisc: '1' })
      )['Receipt']['ReceiptHeader'];
      this.miscRtNo = this.miscRtDetails['Receipt No'] || '';
      this.miscRtDate = this.miscRtDetails['Receipt Date'] ? new Date(this.miscRtDetails['Receipt Date']) : null;
      this.remarks = this.miscRtDetails['Remarks'] || '';
      this.createdBy =
        this.miscRtDetails['Created By'] ?
          `${this.miscRtDetails['Created By']} ${new Date(this.miscRtDetails['Date Created']).formatString('yyyy-MM-dd HH:mm')}`
        : '';
      this.updatedBy =
        this.miscRtDetails['Updated By'] ?
          `${this.miscRtDetails['Updated By']} ${new Date(this.miscRtDetails['Date Updated']).formatString('yyyy-MM-dd HH:mm')}`
        : '';
      this.selectedPayeeType = this.payeeTypeList.find((p) => p['Party Code'] == this.miscRtDetails['Payee Type']);
      await this.getPayeeCodeList(this.selectedPayeeType);
      this.selectedPayeeCode = this.payeeCodeList.find(
        (p) => p[this.payeeCodeKey] == this.miscRtDetails['Customer No'],
      );
      await this.payeeCodeSelected(this.selectedPayeeCode);
      this.payeeName = this.miscRtDetails['Payee'] || '';
      this.selectedPaymentType = this.paymentTypeList.find(
        (p) => p['Payment Type'] == this.miscRtDetails['Payment Type'],
      );
      this.selectedBank = this.bankList.find((b) => b['BankCode'] == this.miscRtDetails['Bank']);
      this.selectedSelfBilledType = this.selfBilledOptions.find((sb) => sb['APIKey'] == this.miscRtDetails['Self-Billed']);
      this.selectedCurrency = this.currencyList.find((c) => c['Currency Code'] == this.miscRtDetails['Currency']);
      this.exchRate = this.miscRtDetails['Rate'] || '1.0000';
      this.exchFactor = this.selectedCurrency['Factor'].toString() || '1';
      this.paymentFAmt = this.miscRtDetails['F.Amount'] || '0.00';
      this.paymentAmt = this.miscRtDetails['Amount'] || '0.00';
      this.bankFCharge = this.miscRtDetails['F.Bank Charge'] || '0.00';
      this.bankCharge = this.miscRtDetails['Bank Charge'] || '0.00';
      this.selectedTaxCode = this.taxCodeList.find((t) => t['Tax Code'] == this.miscRtDetails['Tax Code']) ?? null;
      this.taxRate = this.miscRtDetails['Tax Rate'] || '0';
      this.fTaxAmt = this.miscRtDetails['Tax F.Amount'] || '0.00';
      this.taxAmt = this.miscRtDetails['Tax Amount'] || '0.00';

      if (this.miscRtDetails['Payment Type'] == 'CH') {
        this.chequeNo = this.miscRtDetails['Cheque No'] || '';
        this.chequeDate = this.miscRtDetails['Cheque Date'] ? new Date(this.miscRtDetails['Cheque Date']) : null;
        this.chequeBank = this.miscRtDetails['Cheque Bank'] || '';
      }

      this.miscRtBalance = this.miscRtDetails['Balance'] ? this.$currencyFormat(this.miscRtDetails['Balance']) : '0.00';
      this.miscRtFBalance =
        this.miscRtDetails['F.Balance'] ? this.$currencyFormat(this.miscRtDetails['F.Balance']) : '0.00';

      this.crGLAccDetails = this.miscRtDetails['CreditGL'] || [];
      this.attachmentsList = this.miscRtDetails['Attachments'] || [];
      this.glEntryList = this.miscRtDetails['GL Entry'] || [];
      this.$fullscreenLoading();
    },

    checkInputFields: function () {
      let error = false;

      if (!this.selectedPayeeType) {
        this.payeeTypeErr = 'Payee Type is required!';
        this.requiredError.push({ key: 'payeetype', value: this.payeeTypeErr });
        error = true;
      }

      if (!this.selectedPayeeCode) {
        this.payeeCodeErr = 'Payee Code is required!';
        this.requiredError.push({ key: 'payeecode', value: this.payeeCodeErr });
        error = true;
      }

      if (!this.payeeName.trim()) {
        this.payeeNameErr = 'Payee Name is required!';
        this.requiredError.push({ key: 'payeename', value: this.payeeNameErr });
        error = true;
      }

      if (!this.selectedPaymentType) {
        this.paymentTypeErr = 'Payment Type is required!';
        this.requiredError.push({ key: 'paymenttype', value: this.paymentTypeErr });
        error = true;
      }

      if (this.showChequeInfo) {
        if (!this.chequeNo.trim()) {
          this.chequeNoErr = 'Cheque No is required!';
          this.requiredError.push({ key: 'chequeno', value: this.chequeNoErr });
          error = true;
        }

        if (!this.chequeDate) {
          this.chequeDateErr = 'Cheque Date is required!';
          this.requiredError.push({ key: 'chequedate', value: this.chequeDateErr });
          error = true;
        } else if (this.chequeDate < new Date('1900-01-01')) {
          this.chequeDateErr = 'Invalid Cheque Date!';
          this.requiredError.push({ key: 'chequedate', value: this.chequeDateErr });
          error = true;
        } else if (this.chequeDate > new Date('2079-06-06')) {
          this.chequeDateErr = 'Invalid Cheque Date!';
          this.requiredError.push({ key: 'chequedate', value: this.chequeDateErr });
          error = true;
        }
      }

      if (!this.selectedBank) {
        this.bankErr = 'Bank is required!';
        this.requiredError.push({ key: 'bank', value: this.bankErr });
        error = true;
      }

      if (Number(this.paymentAmt) == 0) {
        this.paymentAmtErr = 'Local Payment Amount invalid!';
        this.requiredError.push({ key: 'paymentamt', value: this.paymentAmtErr });
        error = true;
      }

      if (Number(this.paymentFAmt) == 0) {
        this.paymentFAmtErr = 'Payment Amount invalid!';
        this.requiredError.push({ key: 'paymentfamt', value: this.paymentFAmtErr });
        error = true;
      }

      if (!this.miscRtDate) {
        this.miscRtDateErr = 'Misc. Receipt Date is required!';
        this.requiredError.push({ key: 'mreceiptdate', value: this.miscRtDateErr });
        error = true;
      } else if (this.miscRtDate < new Date('1900-01-01')) {
        this.miscRtDateErr = 'Invalid Misc. Receipt Date!';
        this.requiredError.push({ key: 'mreceiptdate', value: this.miscRtDateErr });
        error = true;
      } else if (this.miscRtDate > new Date('2079-06-06')) {
        this.miscRtDateErr = 'Invalid Misc. Receipt Date!';
        this.requiredError.push({ key: 'mreceiptdate', value: this.miscRtDateErr });
        error = true;
      }

      if (this.bankCharge.trim()) {
        const split = this.bankCharge.split('.');
        if (split[0].length > 14 || split[1].length > 4) {
          this.bankChargeErr = 'Bank Charges cannot exceed 14 digits and 4 decimal places!';
          this.requiredError.push({ key: 'bankchrg', value: this.bankChargeErr });
          error = true;
        }
      }

      if (this.bankFCharge.trim()) {
        const split = this.bankFCharge.split('.');
        if (split[0].length > 14 || split[1].length > 4) {
          this.bankFChargeErr = 'Bank F.Charges cannot exceed 14 digits and 4 decimal places!';
          this.requiredError.push({ key: 'fbankchrg', value: this.bankFChargeErr });
          error = true;
        }
      }
      return error;
    },

    // Get the difference in months between 2 dates (for cheque date)
    getMonthDifference: function (startDate, endDate) {
      const startYear = startDate.getFullYear();
      const startMonth = startDate.getMonth();
      const endYear = endDate.getFullYear();
      const endMonth = endDate.getMonth();

      return (endYear - startYear) * 12 + (endMonth - startMonth);
    },

    getIndicatorName: function (key, value, type) {
      const item = this.summaryItems.find((item) => item['Title'] === key);
      if (item && item['Indicator']) {
        const indicator = item['Indicator'].find((i) => i['code'] === value);

        if (indicator) {
          return type == 'title' ? indicator['name'] : indicator['bg'];
        }
      }
      return item?.Indicator?.[0]?.name || null;
    },

    loadSummaryOfEvents: function () {
      for (const key in this.miscRtDetails) {
        if (Object.prototype.hasOwnProperty.call(this.miscRtDetails, key)) {
          const indicatorName = this.getIndicatorName(key, this.miscRtDetails[key], 'title');
          const bgColour = this.getIndicatorName(key, this.miscRtDetails[key], 'background');
          const element = document.getElementById(key);
          if (element) {
            element.textContent = indicatorName;
            element.style.backgroundColor = bgColour;
          }
        }
      }
    },

    getPayeeCodeList: async function (payeeType) {
      this.payeeCodeList =
        payeeType ?
          await this.$apiCallV2('/Master/GetDetailsMaster', { masterfile: 'payeecode', recordid: payeeType['Table'] })
        : [];

      if (this.payeeCodeList.length > 0) {
        const codeKeys = Object.keys(this.payeeCodeList[0]);
        this.payeeCodeKey = codeKeys[0];
        this.payeeCodeDescKey = codeKeys[1];
      }
    },

    headerForeignCurrLossFocus: async function (state, field, value) {
      if (!state && !this.viewMode && this.showPopup) {
        switch (field) {
          case 'paymentFAmt': {
            const rate = Number(this.exchRate) == 0 ? Number(this.oldRate) : Number(this.exchRate);
            this.oldFAmt = (Number(value) || 0).toFixed(2);
            this.paymentFAmt = (Number(value) || 0).toFixed(2);
            this.paymentAmt = (((Number(value) || 0) * Number(rate)) / Number(this.exchFactor)).toFixed(2);
            this.updateMReceiptBalance();
            break;
          }

          case 'paymentAmt': {
            const rate = Number(this.exchRate) == 0 ? Number(this.oldRate) : Number(this.exchRate);
            this.oldAmt = (Number(value) || 0).toFixed(2);
            this.paymentAmt = (Number(value) || 0).toFixed(2);
            this.paymentFAmt = (((Number(value) || 0) / Number(rate)) * Number(this.exchFactor)).toFixed(2);
            this.updateMReceiptBalance();
            break;
          }

          case 'exchRate':
            this.exchRate = parseFloat(value).toFixed(4);
            if (value <= 0) {
              await this.$showAlert('error', 'Exchange Rate Invalid', 'Exchange rate cannot be 0!');
              this.exchRate = parseFloat(this.oldRate).toFixed(4);
            }
            this.paymentAmt = ((Number(this.paymentFAmt) * Number(this.exchRate)) / Number(this.exchFactor)).toFixed(2);
            this.updateMReceiptBalance();
            break;

          case 'fBankChrg':
            this.bankFCharge = (parseFloat(value) || 0).toFixed(2); // To change NaN values into 0
            this.bankCharge = ((Number(value) || 0) * Number(this.exchRate)).toFixed(2);
            break;

          case 'bankChrg':
            this.bankCharge = (parseFloat(value) || 0).toFixed(2); // To change NaN values into 0
            if (!this.disableFBankChrg) {
              this.bankFCharge = ((Number(value) || 0) / Number(this.exchRate)).toFixed(2);
            }
            break;

          case 'taxRate':
            this.taxAmt = (Number(this.paymentAmt) * (Number(value) / 100)).toFixed(2);
            this.fTaxAmt = (Number(this.paymentFAmt) * (Number(value) / 100)).toFixed(2);
            break;
        }
      }
    },

    updateMReceiptBalance: function () {
      const totalCr = this.crGLAccDetails.reduce((acc, amt) => acc + Number(amt['Amount']), 0);
      const bal = Number(this.paymentAmt) - totalCr;
      this.miscRtBalance = this.$currencyFormat(bal);

      const totalFCr = this.crGLAccDetails.reduce((acc, amt) => acc + Number(amt['F.Amount']), 0);
      const fBal = Number(this.paymentFAmt) - totalFCr;
      this.miscRtFBalance = this.$currencyFormat(fBal);
    },

    deletePopupClicked: async function (e) {
      switch (e) {
        case 'Delete': {
          if (this.deleteMiscRtRemarks.trim() == '') {
            this.deleteMiscRtRemarksErr = 'Please enter delete remarks. ';
            return;
          }

          this.$fullscreenLoading('Deleting Selected Misc. Receipt');
          const deleteBody = {
            action: 'D',
            mreceiptno: this.selectedMRTListing[0]['Misc. Receipt No'],
            reason: this.deleteMiscRtRemarks.trim() || '',
            reinstate: '0',
          };

          const deleteResult = await this.$apiCallV2('/MiscReceipt/SaveMiscReceipt', deleteBody);
          this.$fullscreenLoading();

          this.showDeleteMiscRt = false;

          if (deleteResult[0]['Results'] == 0) {
            this.$showAlert(
              'success',
              'Deleted!',
              `Misc. Receipt ${this.selectedMRTListing[0]['Misc. Receipt No']} was successfully deleted.`,
            );
          } else {
            await this.$showAlert('error', 'Delete Unsuccessful', `${deleteResult[0]['ErrMsg']}`);
          }
          this.miscRtListing = [];
          this.selectedMRTListing = [];
          this.$nextTick(async () => {
            await this.refreshClicked();
          });
          break;
        }

        case 'Cancel':
          this.deleteMiscRtRemarks = '';
          this.showDeleteMiscRt = false;
          break;
      }
    },

    //#region Credit GL Account Tab Functions
    openCrGL: function () {
      this.showCrGLInput = true;
      this.selectedDocType = this.docType[0];
    },

    deleteCrGL: async function () {
      if (this.selectedCrGLDetail.length == 0) {
        this.$showAlert(
          'error',
          'No Credit GL Account Detail Selected',
          'Please select a credit GL account detail to delete',
        );
        return;
      }

      const confirmCancel = await this.$showAlert(
        'warning',
        'Deleting Credit GL Account Detail Record',
        `Are you sure you want to delete this record ?`,
        [
          { text: 'Yes', color: 'red', textColor: 'white' },
          { text: 'No', color: 'lightgray', textColor: 'black' },
        ],
      );
      if (confirmCancel == 'No') return;

      // API will delete all current transactions from ChequeGLPost, then reinsert all 'New' only
      this.selectedCrGLDetail.forEach((cr) => {
        const item = this.crGLAccDetails.find((d) => d['HeaderTxnID'] == cr['HeaderTxnID']);
        item['Action'] = 'D';
        this.deletedCrGLAcc.push(item);
      });
      // temp = this.crGLAccDetails.filter((d) => d['Action'] != 'D');
      const temp = this.crGLAccDetails.filter(
        (d) => !this.deletedCrGLAcc.map((del) => del['HeaderTxnID']).includes(d['HeaderTxnID']),
      );
      this.crGLAccDetails = [];
      this.selectedCrGLDetail = [];

      this.$nextTick(() => {
        this.showCrGLAccList = false;
        this.crGLAccDetails = temp;
        this.updateMReceiptBalance();
        this.showCrGLAccList = true;
      });
    },

    addCrGLAccClicked: async function () {
      // Validations
      this.clearAllCrGLAccErr();
      if (!this.selectedAcc) {
        this.accErr = 'Please select an account no.!';
        return;
      }

      if (
        (this.selectedCurrency['Currency Code'] == 'MYR' && Number(this.creditAmt) == 0) ||
        (this.selectedCurrency['Currency Code'] != 'MYR' && Number(this.creditFAmt) == 0)
      ) {
        this.creditAmtErr = 'Amount Invalid';
        this.creditFAmtErr = 'F. Amount Invalid';
        return;
      }

      const allSavedAcc = this.crGLAccDetails.map((acc) => acc['Account No']);
      if (allSavedAcc.includes(this.selectedAcc['Acc No'])) {
        const duplicateConfirm = await this.$showAlert(
          'warning',
          'Duplicate Credit Account',
          `${this.selectedAcc['Acc No']} already added. Do you want to proceed?`,
          [{ text: 'Yes', color: 'indianred', textColor: 'white' }, { text: 'No' }],
        );

        if (duplicateConfirm == 'No') return;
      }

      if (this.selectedDocType['keyValue'] == 'CHQ' && this.selectedDoc) {
        const dataToPost = {
          chqno: this.selectedDoc['Chq No'],
          payeetype: this.selectedPayeeType['Party Code'],
          payeecode: this.selectedPayeeCode[this.payeeCodeKey],
        };
        this.$fullscreenLoading('Getting Cheque Details');
        const chqDetails = (await this.$apiCallV2('/MiscReceipt/GetChequeRefundDetails', dataToPost))[0];
        this.$fullscreenLoading();

        const proceed = await this.$showAlert(
          'warning',
          `Cheque No. ${this.selectedDoc['Chq No']}`,
          `Total cheque amount: ${Number(chqDetails['Total Amount'] || 0).toFixed(2)}<br>Total previous refund: ${Number(chqDetails['Refund Amount'] || 0).toFixed(2)}<br>This refund: ${Number(this.creditAmt).toFixed(2)}`,
          [
            { text: 'Proceed', color: 'green', textColor: 'white' },
            { text: 'Cancel', color: 'red', textColor: 'white' },
          ],
        );

        if (proceed == 'Cancel') return;
      }

      const dataToAdd = {
        'Account No': this.selectedAcc['Acc No'],
        Amount: this.creditAmt,
        'F.Amount': this.creditFAmt,
        Currency: this.selectedCurrency['Currency Code'],
        Rate: this.exchRate,
        'Document No': this.selectedDoc ? this.selectedDoc[this.getDocListKey] : '',
        'Doc Type': this.selectedDoc ? this.selectedDocType['keyValue'] : '',
        'Payment No': this.selectedPaymentNoFreeText,
        Remark: this.creditRemarks,
        'User ID': '',
        'Date Updated': '',
        HeaderTxnID: `${this.selectedAcc['Acc No']}_${new Date().formatString('yyyyMMddHHmm')}`, // for newly added tracking purposes
        RFTxnID: this.selectedDoc && this.selectedDocType['keyValue'] == 'CHQ' ? this.selectedDoc['Txnid'] : '',
      };

      const temp = this.crGLAccDetails;
      this.crGLAccDetails = [];
      temp.push(dataToAdd);

      this.$nextTick(() => {
        this.showCrGLAccList = false;
        this.crGLAccDetails = temp;
        this.updateMReceiptBalance();
        this.showCrGLAccList = true;
        this.showCrGLInput = false;
        this.clearAllCrGLAccDetails();
      });
    },

    cancelCrGlAccClicked: function () {
      this.showCrGLInput = false;
    },

    creditFocusChanged: function (state, field, value) {
      if (!state && !this.viewMode && this.showPopup) {
        this.creditFAmtErr = '';
        this.creditAmtErr = '';
        switch (field) {
          case 'famt':
            this.creditFAmt = parseFloat(value || 0).toFixed(2);
            this.creditAmt = ((Number(value || 0) * Number(this.exchRate)) / Number(this.exchFactor)).toFixed(2);
            break;
          case 'amt':
            this.creditAmt = parseFloat(value || 0).toFixed(2);
            this.creditFAmt = ((Number(value || 0) / Number(this.exchRate)) * Number(this.exchFactor)).toFixed(2);
            break;
        }
      }
    },

    jobnoRowLimitChanged: async function (focus) {
      if (!focus) {
        this.$fullscreenLoading('Getting Job No');
        this.jobList = [];
        this.jobList = await this.$apiCallV2(`/MiscReceipt/GetCrGLDocs`, {
          top: this.jobnoRowLimit,
          doctype: 'JOB',
        });
        this.$fullscreenLoading();
      }
    },

    paymentnoRowLimitChanged: async function (focus) {
      if (!focus) {
        // this.$fullscreenLoading('Getting Payment No');
        // this.paymentNoList = [];
        // this.paymentNoList = await this.$apiCallV2('/MiscReceipt/GetCrGLDocs', {
        //   top: this.paymentnoRowLimit,
        //   doctype: 'PAYMENT',
        // });
        // this.$fullscreenLoading();

        await this.getPaymentNoList(this.selectedPayeeCode);
      }
    },

    getPaymentNoList: async function (val) {
      this.paymentNoList = [];
      this.paymentNoList = await this.$apiCallV2('/MiscReceipt/GetCrGLDocs', {
        top: this.paymentnoRowLimit,
        doctype: 'PAYMENT',
        payeetype: this.selectedPayeeType['Party Code'],
        payeecode: val[this.payeeCodeKey],
      });
    },

    promptDiscardCrGLAccInput: async function () {
      return await this.$showAlert('warning', 'Credit GL Acc not saved yet', 'Do you wish to discard the changes?', [
        { text: 'Discard', color: 'indianred', textColor: 'white' },
        { text: 'Cancel' },
      ]);
    },

    clearAllCrGLAccDetails: function () {
      this.selectedAcc = null;
      this.selectedDocType = null;
      this.selectedDoc = null;
      this.creditFAmt = '0.00';
      this.creditAmt = '0.00';
      this.creditRemarks = '';
      this.selectedPaymentNo = null;
      this.selectedPaymentNoFreeText = '';

      this.clearAllCrGLAccErr();
    },

    clearAllCrGLAccErr: function () {
      this.accErr = '';
      this.creditFAmtErr = '';
      this.creditAmtErr = '';
    },
    //#endregion

    //#region Attachment Tab Functions
    openAttachmentPopup: function (action) {
      this.clearAllAttData();
      switch (action) {
        case 'new':
          this.attachmentsAction = 'N';
          this.selectedAtt = [];
          this.attachmentsPopupButtons = [{ text: 'Add' }, { text: 'Cancel', color: 'lightgray' }];
          this.attachmentsPopupTitle = 'Add Attachment';
          break;

        case 'edit':
          if (this.selectedAtt.length == 0) {
            this.$showAlert('error', 'No Attachment Selected', 'Please select an Attachment to edit');
            return;
          }
          this.attachmentsAction = 'E';
          this.attachmentsPopupButtons = [{ text: 'Edit' }, { text: 'Cancel', color: 'lightgray' }];
          this.attachmentsPopupTitle = 'Edit Attachment';

          this.documents = {
            docName: this.selectedAtt[0]['File Name'],
            attachementFile: this.selectedAtt[0]['Doc Image'],
            fileType: this.selectedAtt[0]['File Type'],
            docGuid: this.selectedAtt[0]['Document GUID'],
            remarks: this.selectedAtt[0]['Doc Remark'],
            docID: this.selectedAtt[0]['ID'],
          };
          break;
      }
      this.showAttachmentsPopup = true;
    },

    deleteAttachmentClicked: async function () {
      if (this.selectedAtt.length == 0) {
        this.$showAlert('error', 'No Document Selected', 'Please select at least 1 document to delete');
        return;
      }

      const confirmCancel = await this.$showAlert(
        'warning',
        'Deleting Attachment',
        `Are you sure you want to delete this attachment ?`,
        [
          { text: 'Yes', color: 'indianred', textColor: 'white' },
          { text: 'No', color: 'lightgray', textColor: 'black' },
        ],
      );
      if (confirmCancel == 'No') return;

      let existingData = false;
      const tempDeleted = this.deletedAttList;
      this.deletedAttList = [];

      for (const d of this.selectedAtt) {
        const doc = {
          Action: 'D',
          'Document GUID': d['Document GUID'],
        };

        if (d['Document GUID']) {
          tempDeleted.push(doc);
          existingData = true;
        }
      }

      const temp = this.attachmentsList;
      const index = temp.findIndex((item) => {
        return existingData ?
            item['Document GUID'] == this.selectedAtt[0]['Document GUID']
          : item['ID'] == this.selectedAtt[0]['ID'];
      });
      temp.splice(index, 1);
      this.attachmentsList = [];

      this.$nextTick(() => {
        this.deletedAttList = tempDeleted;
        this.attachmentsList = temp;
        this.selectedAtt = [];
      });
    },

    attAdditionalBtnsClicked: async function (btn, data) {
      switch (btn) {
        case 'View':
          this.attachmentsAction = 'V';
          this.selectedAtt = [data];
          await this.projectAttachment({
            'File Name': data['File Name'],
            'MIME Type': null,
            'File Data': data['Doc Image'],
          });
          break;
      }
    },

    attachmentsPopupBtnsClicked: async function (btn) {
      let temp;
      let highestID = 1;
      let doc = null;

      switch (btn) {
        case 'Add':
          if (!this.documents['docName']) {
            this.attachmentsRequiredErrorMessage = 'Please attach at least one file';
            return;
          }

          if (this.attachmentsList.length > 0) {
            let duplicate = false;
            this.attachmentsList.forEach((d) => {
              if (d['File Name'] === this.documents['docName']) duplicate = true;
            });

            if (duplicate) {
              await this.$showAlert('error', 'Selected file already exist!', 'Please select other attachment');
              return;
            }
          }

          temp = [...this.attachmentsList];
          this.attachmentsList = [];
          highestID = temp.reduce((max, d) => (d['ID'] > max ? d['ID'] : max), 1);

          doc = {
            Action: 'N',
            'Doc Remark': this.documents['remarks'] ? this.documents['remarks'] : '',
            'Doc Date': new Date().formatString('yyyy-MM-dd'),
            'File Name': this.documents['docName'] ? this.documents['docName'] : '',
            'File Type': this.documents['fileType'] ? this.documents['fileType'] : '',
            'Doc Image': this.documents['attachmentFile'] ? this.documents['attachmentFile'] : '',
            'Document GUID': this.documents['docGuid'] ? this.documents['docGuid'] : '',
            ID: this.documents['docID'] ? this.documents['docID'] : ++highestID,
            _attachmentAction: 'New',
          };

          this.$nextTick(() => {
            temp.push(doc);
            this.attachmentsList = temp;
            this.oldDoc = this.documents['docName'];
            this.clearAllAttData();
          });
          this.showAttachmentsPopup = false;
          break;

        case 'Edit': {
          if (this.attachmentsList.length > 0) {
            let duplicate = false;
            this.attachmentsList.forEach((d) => {
              if (
                d['File Name'] === this.documents['docName'] &&
                this.documents['docName'] != this.selectedAtt[0]['File Name']
              )
                duplicate = true;
            });

            if (duplicate) {
              this.$showAlert('error', 'Selected file already exist!', 'Please select other attachment');
              return;
            }
          }
          temp = [...this.attachmentsList];
          highestID = temp.reduce((max, d) => (d['ID'] > max ? d['ID'] : max), 1);

          let existingData = false;
          for (const d of this.selectedAtt) {
            doc = {
              Action: d['Action'] ? d['Action'] : 'E',
              'Doc Remark': this.documents['remarks'] ? this.documents['remarks'] : '',
              'Doc Date': new Date().formatString('yyyy-MM-dd'),
              'File Name': this.documents['docName'] ? this.documents['docName'] : '',
              'File Type': this.documents['fileType'] ? this.documents['fileType'] : '',
              'Doc Image': this.documents['attachmentFile'] ? this.documents['attachmentFile'] : '',
              'Document GUID': this.documents['docGuid'] ? this.documents['docGuid'] : '',
              ID: this.documents['docID'] ? this.documents['docID'] : ++highestID,
            };

            if (d['Document GUID']) existingData = true;
          }

          const temp2 = this.attachmentsList;
          const index = temp2.findIndex((item) => {
            return existingData ?
                item['Document GUID'] == this.selectedAtt[0]['Document GUID']
              : item['ID'] == this.selectedAtt[0]['ID'];
          });

          temp2.splice(index, 1, doc);
          this.attachmentsList = [];
          this.$nextTick(() => {
            this.oldDoc = this.documents['docName'];
            this.attachmentsList = temp2;
            this.clearAllAttData();
          });
          this.showAttachmentsPopup = false;
          break;
        }

        case 'Cancel':
          this.showAttachmentsPopup = false;
          this.clearAllAttData();
          break;
      }
    },

    handleFileUpload: async function (file) {
      if (file.length == 0) {
        this.documents['docName'] = '';
        this.documents['fileType'] = '';
        this.documents['attachmentFile'] = '';
      } else {
        this.$fullscreenLoading('Adding File');
        this.documents['docName'] = file[0].name;
        this.documents['fileType'] = file[0].name.substring(file[0].name.indexOf('.'));
        const tempAtt = await file[0].toBase64();
        this.documents['attachmentFile'] = tempAtt.substring(tempAtt.indexOf(',') + 1);
        this.attachmentsRequiredErrorMessage = '';
        this.$fullscreenLoading();
      }
    },

    projectAttachment: async function (file) {
      if (!file && process.env['NODE_ENV'] == 'development') {
        throw new Error('No file was passed to projectAttachment. Check for logic errors');
      }

      this.$fullscreenLoading('Preparing file');
      if (file instanceof File) {
        this.attachmentProjectorFileName = file['name'];
        this.attachmentProjectorType = file['type'];
        this.attachmentProjectorData = URL.createObjectURL(file);
      } else {
        this.attachmentProjectorFileName = file['File Name'];
        this.attachmentProjectorType =
          file['MIME Type'] ||
          (await this.$apiCallV2(`/Receipt/GetMIMEType?filename=${this.attachmentProjectorFileName}`));

        const byteCharacters = atob(
          file['File Data'].startsWith('data:') ? file['File Data'].split(',')[1] : file['File Data'],
        );

        const untypedByteArray = [];
        for (let i = 0; i < byteCharacters.length; i++) {
          untypedByteArray.push(byteCharacters.charCodeAt(i));
        }

        const blob = new Blob([new Uint8Array(untypedByteArray)], { type: this.attachmentProjectorType });
        this.attachmentProjectorData = URL.createObjectURL(blob);
      }

      this.isProjectable = this.attachmentProjectable();
      this.showAttachmentProjector = true;
      this.$fullscreenLoading();
    },

    attachmentProjectable: function () {
      if (!this.attachmentProjectorFileName || !this.attachmentProjectorType) {
        return false;
      }

      const fileNameSplit = this.attachmentProjectorFileName.trim().split('.');
      const fileExtension = fileNameSplit[fileNameSplit.length - 1];
      const projectableExtensions = ['pdf', 'txt', 'json', 'mp3', 'wav', 'mp4', 'webm', 'ogg'];
      return this.attachmentProjectorType.startsWith('image/') || projectableExtensions.includes(fileExtension);
    },

    downloadAttachment: function () {
      const anchor = document.createElement('a');
      anchor['href'] = this.attachmentProjectorData;
      anchor['download'] = this.selectedAtt[0]['File Name'];
      anchor.click();
    },

    closeProjector: function () {
      this.attachmentProjectorType = '';
      this.attachmentProjectorData = '';
      this.showAttachmentProjector = false;
    },

    clearAllAttData: function () {
      this.documents = {
        docName: '',
        attachmentFile: [],
        fileType: '',
        docGuid: '',
        remarks: '',
        docID: 0,
      };
      this.attachmentsRequiredErrorMessage = '';
    },
    //#endregion

    // Print MRt
    openPrintReceipt: function (rtno) {
      let printRtRoute;
      if (rtno) {
        printRtRoute = this.$router.resolve(
          `/Home/${this.$route.params.user}/FINAR/Print Misc Receipt?rtno=${rtno.trim?.() ?? ''}&type=1`,
        );
      } else {
        printRtRoute = this.$router.resolve(`/Home/${this.$route.params.user}/FINAR/Print Misc Receipt?type=1`);
      }
      window.open(printRtRoute.href, '_blank');
    },

    //#region InputQuery/Dropdown selected
    payeeTypeSelected: async function (val) {
      this.$fullscreenLoading('Getting Payee Codes');
      await this.getPayeeCodeList(val);
      this.$fullscreenLoading();
    },

    payeeCodeSelected: async function (val) {
      if (val && this.action != '') {
        this.payeeName = val[this.payeeCodeDescKey];
        this.chequeList = await this.$apiCallV2('/MiscReceipt/GetCrGLDocs', {
          doctype: 'CHQ',
          payeecode: val[this.payeeCodeKey],
        });

        if (this.selectedDocType && this.selectedDocType['keyValue'] == 'CHQ') {
          this.selectedDoc = null;
        }

        await this.getPaymentNoList(val);
      }
    },

    bankSelected: function (val) {
      if (val && this.crGLAccDetails.length == 0) {
        const currentCurrency = this.selectedCurrency['Currency Code'];
        this.selectedCurrency = this.currencyList.find((c) => c['Currency Code'] == (val['BankCurrency'] || 'MYR'));
        // If currency if different than before, then only call currencySelected (will refresh offset docs list)
        if (currentCurrency != this.selectedCurrency['Currency Code']) {
          this.currencySelected(this.selectedCurrency);
        }
      }
    },

    currencySelected: async function (newVal) {
      this.exchRate = parseFloat(newVal?.['Currency Rate']).toFixed(4) || 1.0;
      if (newVal && Number(newVal['Currency Rate']) == 0) {
        await this.$showAlert('error', 'Exchange Rate Invalid', 'Exchange rate cannot be 0!');
        this.exchRate = '1.0000';
      }
      this.exchFactor = newVal?.['Factor'].toString() || '1';
      this.headerForeignCurrLossFocus(false, 'exchRate', this.exchRate);
    },

    taxCodeSelected: function (val) {
      this.taxRate = val ? val['RATE'].toString() : '0';
      this.headerForeignCurrLossFocus(false, 'taxRate', this.taxRate);
    },

    docTypeRadioChanged: function (_val) {
      this.selectedDoc = null;
    },

    paymentTypeSelected: function (newPaymentType) {
      this.bankSelected(this.selectedBank);
      if (newPaymentType['Payment Type'] != 'CH') {
        const errTemp = this.requiredError.filter((err) => ![this.chequeNoErr, this.chequeDateErr].includes(err.value));
        this.requiredError = errTemp;
        this.chequeNoErr = '';
        this.chequeDateErr = '';
      }
    },
  },
  async mounted() {
    let sas = [];
    this.$bus.$on('pageSASControlAccess', (x) => {
      if (x.page == 'Misc Receipt') {
        sas = x['data'];
      }
    });

    const mountedPromises = [];
    let mountedPromisesFulFilledCount = 0;

    mountedPromises.push(
      this.$apiCallV2('/MiscReceipt/GetCrGLDocs', { doctype: 'JOB', top: this.jobnoRowLimit }).then((result) => {
        this.jobList = result;
        this.$topLoading(++mountedPromisesFulFilledCount / mountedPromises.length);
      }),
    );

    mountedPromises.push(
      this.$apiCallV2('/MiscReceipt/GetCrGLDocs', { doctype: 'ASSET' }).then((result) => {
        this.assetList = result;
        this.$topLoading(++mountedPromisesFulFilledCount / mountedPromises.length);
      }),
    );

    mountedPromises.push(
      this.$apiCallV2('/Master/GetListMaster', { masterfile: 'PARTY' }).then((result) => {
        this.payeeTypeList = result;
        this.$topLoading(++mountedPromisesFulFilledCount / mountedPromises.length);
      }),
    );

    mountedPromises.push(
      this.$apiCallV2('/Master/GetListMaster', { masterfile: 'PAYMENTTYPE' }).then((result) => {
        this.paymentTypeList = result;
        this.$topLoading(++mountedPromisesFulFilledCount / mountedPromises.length);
      }),
    );

    mountedPromises.push(
      this.$apiCallV2('/Master/GetListMaster', { masterfile: 'BANK' }).then((result) => {
        this.bankList = result;
        this.$topLoading(++mountedPromisesFulFilledCount / mountedPromises.length);
      }),
    );

    mountedPromises.push(
      this.$apiCallV2('/Master/GetListMaster', { masterfile: 'CURRENCY' }).then((result) => {
        this.currencyList = result;
        this.$topLoading(++mountedPromisesFulFilledCount / mountedPromises.length);
      }),
    );

    mountedPromises.push(
      this.$apiCallV2('/Master/GetListMaster', { masterfile: 'ARTAXCODE' }).then((result) => {
        this.taxCodeList = result;
        this.$topLoading(++mountedPromisesFulFilledCount / mountedPromises.length);
      }),
    );

    mountedPromises.push(
      this.$apiCallV2('/Master/GetSettingsSystem?settingsitem=MISCRECEIPTNOAUTO').then((result) => {
        this.disabledMRTNo = result[0].SettingsValue == 'Y';
        this.$topLoading(++mountedPromisesFulFilledCount / mountedPromises.length);
      }),
    );

    mountedPromises.push(
      this.$apiCallV2('/Master/GetListMaster', { masterfile: 'GLACCOUNT' }).then((result) => {
        this.crAccList = result;
        this.$topLoading(++mountedPromisesFulFilledCount / mountedPromises.length);
      }),
    );

    await Promise.allSettled(mountedPromises).then(async (_promiseOutcomes) => {
      await this.refreshClicked();
      this.menu = this.menu.map((menu) => ({
        ...menu,
        enabled: menu['key'] != 'mnuReinstateMiscReceipt',
      }));

      sas.forEach((k) => {
        const selectedMenu = this.menu.find((m) => m['key'] == k['Control']);
        if (selectedMenu) {
          selectedMenu[k['Properties'].toLowerCase()] = k['PropertiesValue'] != 'False';
        }
      });
    });

    if (this.$route.query['rtno']) {
      this.filterListingBySearch = true;
      this.filterListingByDate = false;
      this.selectedSearchBy = this.searchFilterItems.getSelected('Receipt #', 'Search By');
      this.selectedSearchCriteria = this.searchCriteriaItems.getSelected('EQUALS', 'name');
      this.searchValue = this.$route.query['rtno'];
      await this.refreshClicked();
      this.selectedMRTListing = this.miscRtListing.filter((r) => r['Misc. Receipt No'] === this.$route.query['rtno']);
      // SAS control
      const menuTitle = this.$route.query['action'] == 'E' ? 'Edit Misc. Receipt' : 'View Misc. Receipt';
      const selectedMenu = this.menu.getSelected(menuTitle, 'title');
      if (!selectedMenu['enabled']) {
        this.$showAlert('warning', 'Access Denied', `You do not have permission to ${menuTitle}`);
        return;
      }
      this.$nextTick(() => {
        this.menuClicked({ 
          title: menuTitle 
        });
      });
    }

    const cocode = (await this.$apiCallV2('/Login/GetDetails'))?.CoCode;
    const companyDetails = (
      await this.$apiCallV2('/Master/GetDetailsMaster', { masterfile: 'COMPANY', recordid: cocode })
    )['Company'];
    this.companyGSTEnabled = companyDetails?.CompanyGST2Enabled;
    this.companyGSTEffectiveDate = companyDetails?.CompanyGST2EffectiveDate;
    this.companySSTEffectiveDate = companyDetails?.CompanyGSTEffectiveDate;

    this.payeeTypeList = this.payeeTypeList.map((p) => {
      return {
        ...p,
        'Code - Party': `${p['Party Code']} - ${p['Party']}`,
      };
    });

    this.paymentTypeList = this.paymentTypeList.map((p) => {
      return {
        ...p,
        'Payment Type - Description': `${p['Payment Type']} - ${p['Description']}`,
      };
    });

    this.bankList = this.bankList.map((b) => {
      return {
        ...b,
        'BankCode - BankName': `${b['BankCode']} - ${b['BankName']}`,
      };
    });

    this.taxCodeList = this.taxCodeList.map((t) => {
      return {
        ...t,
        'Tax Code - Description': `${t['Tax Code']} - ${t['Description']}`,
      };
    });

    this.crAccList = this.crAccList.map((c) => {
      return {
        ...c,
        'Acc No - Description': `${c['Acc No']} - ${c['Description']}`,
      };
    });
  },
  watch: {
    selectedCurrency: function (val) {
      this.oldRate = val ? val['Currency Rate'] : '';
    },

    exchRate: function (newVal, oldVal) {
      if (Number(oldVal) != Number(newVal)) {
        this.oldRate = Number(oldVal).toFixed(4);
      }
    },

    selectedPayeeCode: function (val) {
      this.docType.find((d) => d['keyValue'] == 'CHQ')['isDisabled'] = !val;

      if (this.showCrGLInput && this.selectedDocType && this.selectedDocType['keyValue'] == 'CHQ' && !val) {
        this.selectedDocType = null;
        this.selectedDocType = this.docType[0];
      }
    },

    selectedBank: function (val) {
      if (val && val['BankCurrency'] == 'MYR') {
        this.bankFCharge = '0.00';
      }
    },

    selectedMRTListing: function (val) {
      if (val.length > 0 && val[0]['Indicator'] === 'C') {
        this.menu.find((m) => m['key'] == 'mnuReinstateMiscReceipt')['enabled'] = true;
      } else {
        this.menu.find((m) => m['key'] == 'mnuReinstateMiscReceipt')['enabled'] = false;
      }

      if (val.length > 0 && val[0]['Authorised'] === 'A') {
        this.menu.find((m) => m['key'] == 'mnUnAuthorized')['visible'] = true;
        this.menu.find((m) => m['key'] == 'mnAuthorized')['visible'] = false;
      } else if (val.length > 0 && val[0]['Authorised'] === 'U') {
        this.menu.find((m) => m['key'] == 'mnUnAuthorized')['visible'] = false;
        this.menu.find((m) => m['key'] == 'mnAuthorized')['visible'] = true;
      } else {
        this.menu.find((m) => m['key'] == 'mnUnAuthorized')['visible'] = false;
        this.menu.find((m) => m['key'] == 'mnAuthorized')['visible'] = true;
        this.menu.find((m) => m['key'] == 'mnAuthorized')['enabled'] = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
@import '@/css/all.scss';

.mrt-main {
  display: flex;
  font-size: 12px;
  padding: 10px 45px;

  > .left-menu {
    display: flex;
    justify-content: center;
  }

  > .right-container {
    width: 100%;
    margin-left: 5px;
    display: flex;
    flex-direction: column;
    overflow: hidden;

    > .top-section,
    > .bottom-section {
      background-color: white;
      border-radius: 5px;
      padding: 5px;
    }

    > .top-section {
      display: flex;
      align-items: center;
      justify-content: flex-end;

      > .filter-desc {
        padding-right: 8px;
      }
    }

    > .bottom-section {
      height: calc(100% - 38px - 5px);
      margin-top: 5px;
    }
  }

  > .attachments-popup-container {
    > .attachments-popup {
      ::v-deep {
        // deep needed to target the content class inside the PopupV2 component
        .content {
          // NOT the content class for slot, but content class inside PopupV2 itself
          overflow: visible;
        }
      }

      .attachments-popup-content {
        overflow: visible;

        > .attached-file {
          text-align: left;
          margin-bottom: 8px;

          > .file-link {
            cursor: pointer;
            user-select: none;

            &:hover {
              text-decoration: underline;
            }

            &:active {
              background-color: black;
              color: white;
            }
          }
        }
      }
    }
  }

  > .attachment-projector {
    height: 100%;
    width: 100%;
    position: absolute;
    top: 0;
    left: 0;
    z-index: 99999;
    background-color: rgba(0, 0, 0, 0.8);
    display: flex;
    justify-content: center;
    align-items: center;

    > .attachment-projection {
      height: 95%;
      width: 80%;

      > object {
        height: 100%;
        width: 100%;
        object-fit: scale-down;
      }

      > .attachment-not-projectable-container {
        height: 100%;
        width: 100%;
        background-color: rgba(0, 0, 0, 0.7);
        display: flex;
        flex-direction: column;
        justify-content: center;
        align-items: center;
        text-align: center;
        font-size: 40px;

        > span {
          margin: 10px 0 10px 0;
          color: white;
        }

        > button {
          height: 50px;
          width: 150px;
          margin: 10px 0 10px 0;
          font-size: 16px;

          > img {
            height: 24px;
            width: 24px;
          }
        }
      }
    }

    > .close-button {
      height: 50px;
      width: 50px;
      position: absolute;
      top: 10px;
      right: 10px;
      border-radius: 100px;
      background-color: white;
      text-align: center;
      font-size: xx-large;
      cursor: pointer;
      user-select: none;
    }
  }
}

.button-filter-component {
  margin-right: 5px;
  position: relative;

  .filter-popup {
    display: flex;
    flex-direction: column;
    z-index: 10;

    > button {
      margin-top: 5px;
      width: fit-content;
      align-self: flex-end;
    }
  }
}

.summary-row {
  display: flex;
  flex-direction: row;
  width: fit-content;

  > :nth-child(1) {
    margin-right: 5px;
  }
}

.summary-block {
  position: relative;
  margin-bottom: 2px;
  margin-right: 3px;

  > .summary-item {
    border: 1px solid rgba(128, 128, 128, 0.5);
    max-width: 120px;
    min-width: 120px;
    -webkit-line-clamp: 2;
    display: -webkit-box;
    text-overflow: ellipsis;
    overflow: hidden;
    -webkit-box-orient: vertical;
    cursor: default;
    padding: 3px;
  }

  > .summary-item-printer {
    border: 1px solid rgba(128, 128, 128, 0.5);
    max-width: 100px;
    -webkit-line-clamp: 2;
    display: -webkit-box;
    text-overflow: ellipsis;
    overflow: hidden;
    -webkit-box-orient: vertical;
    cursor: default;
    padding: 3px;
  }

  > .milestone-tooltip {
    position: absolute;
    text-overflow: wrap;
    height: 100%;
    width: 100%;
    top: 0;
    left: 0;
  }
}

.legend-items {
  display: flex;
  align-items: center;
  margin-right: 3px;

  > .legend-box {
    height: 10px;
    width: 10px;
    border: 1px solid rgba(128, 128, 128, 0.5);
    margin-right: 3px;
  }
}

.mrt-entries {
  height: calc(100% - 27px);
  width: 100%;
  overflow: scroll;

  .mrt-header {
    display: flex;
    flex-direction: row;
    justify-content: space-between;

    > .header-containers {
      width: 100%;
      margin-right: 10px;
    }
  }
}

.foreign-currency-main {
  font-size: 12px;
  padding: 10px;
  border-radius: 10px;
  position: relative;
  background-color: rgb(225, 255, 255);
}

.foreign-currency-controls {
  position: relative;
  z-index: 101;
}

.foreign-currency-row {
  position: relative;
  display: flex;
  align-items: center;
  // min-height: 30px;
  flex-direction: row;

  > :not(:first-child) {
    margin-left: 5px;
  }

  > .foreign-currency-column {
    flex: 1;
  }
}

.foreign-currency-background {
  position: absolute;
  top: 0;
  left: 0;
  height: 100%;
  width: 100%;
  display: flex;
  flex-direction: row;
  z-index: 1;

  > .background-left {
    // flex: 1;
    width: calc(33.33% + 4px);
    // background-color: rgb(225, 255, 255);
    border-right: 3px solid white;
  }

  > .background-middle {
    flex: 1;
    // background-color: transparent;
    border-left: 3px solid white;
    border-right: 3px solid white;
  }

  > .background-right {
    // flex: 1;
    width: calc(33.33% + 4px);
    // background-color: rgb(225, 255, 255);
    border-left: 3px solid white;
  }
}

.tab-slot {
  display: flex;
  height: 100%;

  > .tab-details-styling {
    margin-right: 3px;
    height: 100%;
    overflow: scroll;

    .tab-buttons {
      display: flex;
      flex-direction: row;
      margin: 5px 5px;
      align-items: center;

      > button {
        margin-right: 5px;
        height: fit-content;
      }
    }
  }

  > .side-entry-open {
    height: 100%;
    border-left: 1px solid black;
    overflow: scroll;
    margin-left: 5px;
    padding-left: 10px;

    .title-button {
      display: flex;
      flex-direction: row;
      justify-content: space-between;
      height: 50px;
      margin: 5px 0px;
      padding: 5px 0px;
      // position: sticky;
      // top: 0;
      // z-index: 5;
      // background-color: white;

      .buttons-arr {
        display: flex;

        button {
          margin-right: 5px;
          height: fit-content;
        }
      }
    }
  }
}

.total-block {
  border: 1px solid rgba(128, 128, 128, 0.5);
  padding: 5px 10px;
  align-content: space-evenly;
  min-width: 210px;
  padding-right: 5px;
  display: inline;
  text-align: right;
}
</style>
